import React, { useState, useEffect } from 'react';
import Pagination from './Pagination';
import CreateUserWizard from './CreateUserWizard';
import Select from '../../../components/ui/Select';
import {
  Users,
  Shield,
  Key,
  Loader2,
  AlertTriangle,
  Eye,
  Edit,
  Trash2,
  UserCheck,
  UserX,
  Activity,
  Search,
  Filter,
  X,
  RotateCcw,
  CheckCircle,
  FileSpreadsheet
} from 'lucide-react';


// Import API functions

import { getAllUsers, updateUser, deleteUser, getUserById } from '../../../api/admin';
import { getUserProfileById } from '../../../api/profile';
import {
  getAllPermissions,
  getAllPolicies,
  createPolicy,
  updatePolicy
} from '../../../api/iam';
import {
  listGroups,
  createGroup,
  updateGroup,
  deleteGroup,
  getGroupById
} from '../../../api/group';
import {
  getActivityLogs,
  exportActivityLogs,
  cleanActivityLogs
} from '../../../api/activityLogs';

const AccessManagement = () => {
  const [activeTab, setActiveTab] = useState('overview');

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data states
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]); // Store all permissions for frontend pagination
  const [policies, setPolicies] = useState([]);
  const [allPolicies, setAllPolicies] = useState([]); // Store all policies for full selection
  const [policySearch, setPolicySearch] = useState('');
  const [groups, setGroups] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);

  // Pagination states
  const [permissionsPagination, setPermissionsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [policiesPagination, setPoliciesPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [groupsPagination, setGroupsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [activityLogsPagination, setActivityLogsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Activity Logs filter states
  const [activityFilters, setActivityFilters] = useState({
    search: '',
    userId: '',
    userEmail: '',
    userRole: '',
    activityType: '',
    resource: '',
    success: '',
    dateFrom: '',
    dateTo: '',
    sortBy: 'timestamp',
    sortOrder: 'desc'
  });
  const [activityFilterOptions, setActivityFilterOptions] = useState({
    userRoles: ['Admin', 'Doctor', 'Nurse', 'Receptionist'],
    activityTypes: [],
    resources: []
  });
  const [showActivityFilters, setShowActivityFilters] = useState(false);

  // Group modal states
  const [isCreateGroupModalOpen, setIsCreateGroupModalOpen] = useState(false);
  const [isEditGroupModalOpen, setIsEditGroupModalOpen] = useState(false);
  const [isViewGroupModalOpen, setIsViewGroupModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [viewGroupData, setViewGroupData] = useState(null);
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: '',
    selectedPolicyIds: [],
    selectedPermissionActions: []
  });
  const [groupActionLoading, setGroupActionLoading] = useState(false);

  // Create user modal states
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);

  // Edit user modal states
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUserFormData, setEditUserFormData] = useState({
    email: '',
    name: '',
    role: 'Admin',
    status: 'active',
    selectedGroups: [],
    selectedPermissions: []
  });
  const [userActionLoading, setUserActionLoading] = useState(false);
  const [editUserError, setEditUserError] = useState(null);

  // View user profile modal states
  const [isViewUserProfileOpen, setIsViewUserProfileOpen] = useState(false);
  const [viewUserProfileData, setViewUserProfileData] = useState(null);
  const [viewUserProfileLoading, setViewUserProfileLoading] = useState(false);

  // Permissions popup modal states
  const [isPermissionsPopupOpen, setIsPermissionsPopupOpen] = useState(false);
  const [popupPermissions, setPopupPermissions] = useState([]);
  const [popupTitle, setPopupTitle] = useState('');
  const [popupSearch, setPopupSearch] = useState('');

  // Expanded user names state
  const [expandedUserNames, setExpandedUserNames] = useState(new Set());

  // User filter states
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [userRoleFilter, setUserRoleFilter] = useState('');
  const [userStatusFilter, setUserStatusFilter] = useState('');
  const [showUserFilters, setShowUserFilters] = useState(true);

  // Filtered users logic
  const filteredUsers = users.filter(user => {
    const matchesSearch = userSearchTerm === '' ||
      (user.name && user.name.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
      (user.email && user.email.toLowerCase().includes(userSearchTerm.toLowerCase()));

    const matchesRole = userRoleFilter === '' || user.role === userRoleFilter;
    const matchesStatus = userStatusFilter === '' || (user.status || '').toLowerCase() === userStatusFilter.toLowerCase();

    return matchesSearch && matchesRole && matchesStatus;
  });

  // Stats state
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalGroups: 0,
    totalPermissions: 0,
    totalPolicies: 0,
    totalActivityLogs: 0
  });

  // Data fetching functions
  const fetchPermissions = async (page = 1, limit = 10) => {
    try {
      console.log(`Fetching permissions: page=${page}, limit=${limit}`); // Debug log

      // If we have all permissions cached, use frontend pagination
      if (allPermissions.length > 0) {
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedPermissions = allPermissions.slice(startIndex, endIndex);

        setPermissions(paginatedPermissions);
        setPermissionsPagination({
          page: page,
          limit: limit,
          total: allPermissions.length,
          pages: Math.ceil(allPermissions.length / limit)
        });
        return;
      }

      // Fetch all permissions from API once
      const permissionsResponse = await getAllPermissions({ page: 1, limit: 1000 }); // Get all permissions
      console.log('Permissions API Response:', permissionsResponse); // Debug log

      const permissionsArray = permissionsResponse.data?.data?.permissions || [];

      // Store all permissions for frontend pagination
      setAllPermissions(permissionsArray);

      // Apply frontend pagination
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedPermissions = permissionsArray.slice(startIndex, endIndex);

      setPermissions(paginatedPermissions);
      setPermissionsPagination({
        page: page,
        limit: limit,
        total: permissionsArray.length,
        pages: Math.ceil(permissionsArray.length / limit)
      });

      console.log('Frontend pagination applied:', {
        total: permissionsArray.length,
        page: page,
        limit: limit,
        pages: Math.ceil(permissionsArray.length / limit),
        displayedCount: paginatedPermissions.length
      }); // Debug log

    } catch (err) {
      console.error('Failed to fetch permissions:', err);
    }
  };

  const fetchPolicies = async (page = 1, limit = 10) => {
    try {
      console.log(`Fetching policies: page=${page}, limit=${limit}`);

      // If we have all policies cached, use frontend pagination
      if (allPolicies.length > 0) {
        const startIndex = (page - 1) * limit;
        const endIndex = startIndex + limit;
        const paginatedPolicies = allPolicies.slice(startIndex, endIndex);

        setPolicies(paginatedPolicies);
        setPoliciesPagination({
          page,
          limit,
          total: allPolicies.length,
          pages: Math.ceil(allPolicies.length / limit)
        });
        return;
      }

      // Fetch all policies from API once
      const policiesResponse = await getAllPolicies({ page: 1, limit: 1000 });
      console.log('Policies API Response:', policiesResponse);
      const policiesArray = policiesResponse.data?.data?.policies || [];

      // Cache all policies for full selection in modals
      setAllPolicies(policiesArray);

      // Apply frontend pagination
      const startIndex = (page - 1) * limit;
      const endIndex = startIndex + limit;
      const paginatedPolicies = policiesArray.slice(startIndex, endIndex);
      setPolicies(paginatedPolicies);
      setPoliciesPagination({
        page,
        limit,
        total: policiesArray.length,
        pages: Math.ceil(policiesArray.length / limit)
      });
    } catch (err) {
      console.error('Failed to fetch policies:', err);
    }
  };

  const fetchGroups = async (page = 1, limit = 10) => {
    try {
      const groupsResponse = await listGroups({ page, limit });
      console.log('Groups API Response:', groupsResponse); // Debug log
      setGroups(groupsResponse.data?.data?.groups || []);
      setGroupsPagination(groupsResponse.data?.data?.pagination || {
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
      });
    } catch (err) {
      console.error('Failed to fetch groups:', err);
    }
  };

  const fetchActivityLogs = async (page = 1, limit = 10, filters = {}) => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 Fetching activity logs with filters:', filters);
      console.log('📋 Current activityFilters state:', activityFilters);

      // Prepare query params to match backend API expectations
      const queryParams = {
        page: page || 1,
        limit: limit || 10,
        sortBy: filters.sortBy || 'timestamp',
        sortOrder: filters.sortOrder || 'desc'
      };

      // Add non-empty filter parameters
      if (filters.userId && filters.userId !== '') {
        queryParams.userId = filters.userId;
      }
      if (filters.userEmail && filters.userEmail !== '') {
        queryParams.userEmail = filters.userEmail;
      }
      if (filters.userRole && filters.userRole !== '') {
        queryParams.userRole = filters.userRole;
      }
      if (filters.activityType && filters.activityType !== '') {
        queryParams.activityType = filters.activityType;
      }
      if (filters.resource && filters.resource !== '') {
        queryParams.resource = filters.resource;
      }
      if (filters.success && filters.success !== '') {
        queryParams.success = filters.success;
      }
      if (filters.search && filters.search !== '') {
        queryParams.search = filters.search;
      }
      if (filters.dateFrom && filters.dateFrom !== '') {
        queryParams.dateFrom = filters.dateFrom;
      }
      if (filters.dateTo && filters.dateTo !== '') {
        queryParams.dateTo = filters.dateTo;
      }

      console.log('🚀 Making API call with queryParams:', queryParams);
      const activityLogsResponse = await getActivityLogs(queryParams);

      const responseData = activityLogsResponse?.data;

      if (!responseData) {
        console.warn('⚠️ No data in response');
        setActivityLogs([]);
        return;
      }

      const logsData = responseData.data || [];
      const paginationData = responseData.pagination || {
        page: page,
        limit: limit,
        total: 0,
        pages: Math.max(1, Math.ceil((responseData.total || 0) / limit))
      };

      setActivityLogs(logsData);
      setActivityLogsPagination({
        ...paginationData,
        page: page,
        limit: limit
      });

    } catch (err) {
      console.error('❌ Failed to fetch activity logs:', err);
      setError(`Failed to fetch activity logs: ${err.message}`);
      setActivityLogs([]);
    } finally {
      setLoading(false);
    }
  };

  // Export activity logs to Excel (.xlsx)
  const handleExportActivityLogs = async () => {
    try {
      setLoading(true);

      // Build query params from active filters
      const queryParams = {
        format: 'json',
        sortBy: activityFilters.sortBy || 'timestamp',
        sortOrder: activityFilters.sortOrder || 'desc'
      };
      if (activityFilters.userId)       queryParams.userId       = activityFilters.userId;
      if (activityFilters.userEmail)    queryParams.userEmail    = activityFilters.userEmail;
      if (activityFilters.userRole)     queryParams.userRole     = activityFilters.userRole;
      if (activityFilters.activityType) queryParams.activityType = activityFilters.activityType;
      if (activityFilters.resource)     queryParams.resource     = activityFilters.resource;
      if (activityFilters.success)      queryParams.success      = activityFilters.success;
      if (activityFilters.search)       queryParams.search       = activityFilters.search;
      if (activityFilters.dateFrom)     queryParams.dateFrom     = activityFilters.dateFrom;
      if (activityFilters.dateTo)       queryParams.dateTo       = activityFilters.dateTo;

      const response = await exportActivityLogs(queryParams);
      // backend wraps data inside { exportDate, data: [...] }
      const logs = response?.data?.data ?? response?.data ?? [];

      // ── Build Excel rows ──────────────────────────────────────────────────
      const ACTIVITY_LABELS = {
        login:'User Login',logout:'User Logout',password_change:'Password Changed',
        create:'Record Created',read:'Record Viewed',update:'Record Updated',delete:'Record Deleted',
        user_create:'User Account Created',user_update:'User Account Updated',user_delete:'User Account Deleted',
        appointment_create:'Appointment Scheduled',appointment_update:'Appointment Updated',appointment_cancel:'Appointment Cancelled',
        patient_create:'New Patient Registered',patient_update:'Patient Record Updated',
        prescription_create:'Prescription Issued',prescription_update:'Prescription Updated',
        payment_create:'Payment Recorded',payment_update:'Payment Updated',
        invoice_create:'Invoice Created',invoice_update:'Invoice Updated',invoice_cancel:'Invoice Cancelled',
        billing_create:'Billing Record Created',billing_update:'Billing Record Updated',
        emr_create:'Medical Record Created',emr_update:'Medical Record Updated',emr_delete:'Medical Record Deleted',
        lab_report_create:'Lab Report Created',lab_report_update:'Lab Report Updated',
        export:'Data Exported',config_change:'Settings Changed',
      };
      const RESOURCE_LABELS_MAP = {
        patients:'Patients',appointments:'Appointments',users:'Users',admin_users:'Users',
        prescriptions:'Prescriptions',lab_reports:'Lab Reports',billing:'Billing',
        invoices:'Invoices',payments:'Payments',inventory:'Inventory',medicines:'Medicines',
        imaging:'Imaging',emr:'Medical Records',immunizations:'Immunizations',
        beds:'Beds',hr:'HR',payroll:'Payroll',settings:'Settings',auth:'Authentication',
        activity_logs:'Activity Logs',reports:'Reports',availability:'Availability',
        slogans:'Slogans',master_data:'Master Data',complete_service:'Service Completion',
      };
      const MONGO_ID = /^[0-9a-f]{24}$/i;
      const RAW_HTTP = /^(GET|POST|PUT|PATCH|DELETE)\s+\//i;

      const fmtType  = t => ACTIVITY_LABELS[t] || (t ? t.replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase()) : '—');
      const fmtRes   = r => {
        if (!r) return '—';
        const parts = r.split('_').filter(p => !MONGO_ID.test(p));
        const key   = parts.join('_').toLowerCase();
        return RESOURCE_LABELS_MAP[key] || parts.map(p=>p.charAt(0).toUpperCase()+p.slice(1)).join(' ');
      };
      const fmtAction = a => {
        if (!a) return '—';
        if (RAW_HTTP.test(a)) {
          const [method] = a.split(' ');
          const VERBS = {GET:'Viewed',POST:'Created/Submitted',PUT:'Updated',PATCH:'Updated',DELETE:'Deleted'};
          return VERBS[method] || a;
        }
        return a;
      };
      const fmtDate = ts => ts ? new Date(ts).toLocaleString('en-IN',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';

      const rows = logs.map((log, i) => ({
        'S.No':           i + 1,
        'Date & Time':    fmtDate(log.timestamp),
        'User Name':      log.userName  || '—',
        'User Email':     log.userEmail || '—',
        'User Role':      log.userRole  || '—',
        'Activity Type':  fmtType(log.activityType),
        'Action':         fmtAction(log.action),
        'Resource':       fmtRes(log.resource),
        'Status':         log.success ? 'Success' : 'Failed',
        'IP Address':     log.ipAddress !== 'unknown' ? (log.ipAddress || '—') : '—',
        'Error Message':  log.errorMessage || '—',
      }));

      // ── Write worksheet ──────────────────────────────────────────────────
      const XLSX = await import('xlsx');
      const ws   = XLSX.utils.json_to_sheet(rows);

      // Set column widths for readability
      ws['!cols'] = [
        { wch: 6 },  // S.No
        { wch: 22 }, // Date & Time
        { wch: 20 }, // User Name
        { wch: 28 }, // User Email
        { wch: 14 }, // User Role
        { wch: 26 }, // Activity Type
        { wch: 24 }, // Action
        { wch: 20 }, // Resource
        { wch: 10 }, // Status
        { wch: 16 }, // IP Address
        { wch: 30 }, // Error Message
      ];

      const wb       = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Activity Logs');

      const filename = `activity-logs-${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, filename);

      console.log(`✅ Exported ${rows.length} activity logs to ${filename}`);
    } catch (err) {
      console.error('❌ Failed to export activity logs:', err);
      setError(`Failed to export activity logs: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Clear / delete old activity logs
  const handleClearLogs = async () => {
    setClearLogsLoading(true);
    setClearLogsResult(null);
    try {
      let payload;
      if (clearLogsMode === 'custom') {
        if (!clearLogsDateFrom || !clearLogsDateTo) return;
        payload = { dateFrom: new Date(clearLogsDateFrom).toISOString(), dateTo: new Date(clearLogsDateTo).toISOString() };
      } else {
        // Delete all logs older than N days (from epoch up to the cutoff date)
        const cutoff = new Date();
        cutoff.setDate(cutoff.getDate() - clearLogsDays);
        payload = { dateFrom: new Date(0).toISOString(), dateTo: cutoff.toISOString() };
      }
      const response = await cleanActivityLogs(payload);
      const deleted = response?.data?.data?.deletedCount ?? response?.deletedCount ?? 0;
      setClearLogsResult({ deletedCount: deleted });
      // Refresh the logs list
      await fetchActivityLogs(1, activityLogsPagination.limit, activityFilters);
    } catch (err) {
      console.error('❌ Failed to clear activity logs:', err);
      setError(`Failed to clear activity logs: ${err.message}`);
      setShowClearLogsModal(false);
    } finally {
      setClearLogsLoading(false);
    }
  };

  // Export permissions to Excel (.xlsx)
  const handleExportPermissions = async () => {
    try {
      const source = allPermissions.length > 0 ? allPermissions : permissions;
      if (!source.length) return;

      const rows = source.map((p, i) => ({
        'S.No':        i + 1,
        'Permission':  p.name        || '—',
        'Description': p.description || '—',
        'Category':    p.category    || 'General',
        'Usage':       p.usage       || 'Not used',
        'Created On':  p.createdAt   ? new Date(p.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—',
      }));

      const XLSX = await import('xlsx');
      const ws   = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [
        { wch: 6 },  // S.No
        { wch: 36 }, // Permission
        { wch: 44 }, // Description
        { wch: 18 }, // Category
        { wch: 16 }, // Usage
        { wch: 16 }, // Created On
      ];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Permissions');
      XLSX.writeFile(wb, `permissions-${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (err) {
      console.error('❌ Failed to export permissions:', err);
      setError(`Failed to export permissions: ${err.message}`);
    }
  };

  // Fetch filter options for activity logs
  const fetchActivityFilterOptions = async () => {
    try {
      // Note: This API endpoint might not exist yet, so we'll use default options
      setActivityFilterOptions(prev => ({
        ...prev,
        activityTypes: ['LOGIN', 'LOGOUT', 'CREATE', 'READ', 'UPDATE', 'DELETE', 'PERMISSION_CHECK'],
        resources: ['User', 'Patient', 'Appointment', 'Prescription', 'Note', 'Visit', 'Group', 'Policy']
      }));
    } catch (err) {
      console.error('Error fetching activity filter options:', err);
    }
  };

  const fetchAllData = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('Starting to fetch all data...'); // Debug log

      // Fetch users
      try {
        const usersResponse = await getAllUsers({ limit: 100 });
        console.log('Users API Response:', usersResponse); // Debug log
        setUsers(usersResponse.data?.users || []);
        console.log('Users fetched:', usersResponse.data?.users); // Debug log

      } catch (err) {
        console.error('Failed to fetch users:', err);
      }

      // Fetch permissions with pagination
      await fetchPermissions(1, 10);

      // Fetch policies with pagination  
      await fetchPolicies(1, 10);

      // Fetch groups with pagination
      await fetchGroups(1, 10);

      // Fetch activity logs with pagination
      console.log('📥 Loading initial activity logs...');
      await fetchActivityFilterOptions();
      await fetchActivityLogs(1, 10, activityFilters);

      console.log('Final state after fetching:', {
        usersCount: users.length,
        permissionsCount: permissions.length,
        policiesCount: policies.length,
        groupsCount: groups.length,
        activityLogsCount: activityLogs.length
      }); // Debug log
    } catch (err) {
      console.error('Failed to fetch data:', err);
      setError('Failed to load access management data');
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers
  const handlePermissionsPageChange = async (newPage) => {
    setPermissionsPagination(prev => ({ ...prev, page: newPage }));
    await fetchPermissions(newPage, permissionsPagination.limit);
  };

  const refreshPermissionsCache = async () => {
    setAllPermissions([]); // Clear cache to force refresh
    await fetchPermissions(1, permissionsPagination.limit);
  };

  const handlePoliciesPageChange = async (newPage) => {
    setPoliciesPagination(prev => ({ ...prev, page: newPage }));
    await fetchPolicies(newPage, policiesPagination.limit);
  };

  const handleGroupsPageChange = async (newPage) => {
    setGroupsPagination(prev => ({ ...prev, page: newPage }));
    await fetchGroups(newPage, groupsPagination.limit);
  };

  const handleActivityLogsPageChange = async (newPage) => {
    console.log('📄 Page change requested:', { from: activityLogsPagination.page, to: newPage });
    setActivityLogsPagination(prev => ({ ...prev, page: newPage }));
    await fetchActivityLogs(newPage, activityLogsPagination.limit, activityFilters);
  };

  // Activity logs filter handlers
  const handleActivityFilterChange = (key, value) => {
    console.log(`🔧 Filter changed: ${key} = ${value}`);
    setActivityFilters(prev => {
      const newFilters = { ...prev, [key]: value };
      console.log('📝 Updated filters:', newFilters);
      return newFilters;
    });
  };

  const handleActivityFiltersApply = async () => {
    try {
      console.log('🎯 Applying filters:', activityFilters);

      // Reset to first page when applying filters
      setActivityLogsPagination(prev => ({ ...prev, page: 1 }));

      // Apply the filters with page 1 and current limit
      await fetchActivityLogs(1, activityLogsPagination.limit, activityFilters);

    } catch (err) {
      console.error('❌ Error applying filters:', err);
      setError('Failed to apply filters');
    }
  };

  const handleActivityFiltersReset = async () => {
    try {
      console.log('🔄 Resetting filters...');
      const resetFilters = {
        search: '',
        userId: '',
        userEmail: '',
        userRole: '',
        activityType: '',
        resource: '',
        success: '',
        dateFrom: '',
        dateTo: '',
        sortBy: 'timestamp',
        sortOrder: 'desc'
      };
      setActivityFilters(resetFilters);
      setActivityLogsPagination(prev => ({ ...prev, page: 1 }));
      await fetchActivityLogs(1, activityLogsPagination.limit, resetFilters);
      console.log('✅ Filters reset successfully');
    } catch (err) {
      console.error('❌ Error resetting filters:', err);
      setError('Failed to reset filters');
    }
  };

  const handleActivitySearch = async (searchTerm) => {
    try {
      console.log('🔍 Search triggered with term:', searchTerm);
      const newFilters = { ...activityFilters, search: searchTerm };
      setActivityFilters(newFilters);
      setActivityLogsPagination(prev => ({ ...prev, page: 1 }));
      await fetchActivityLogs(1, activityLogsPagination.limit, newFilters);
    } catch (err) {
      console.error('❌ Error in search:', err);
      setError('Search failed');
    }
  };

  // Create Group handler
  const handleCreateGroup = async () => {
    if (!groupFormData.name.trim()) {
      setError('Group name is required');
      return;
    }

    setGroupActionLoading(true);
    try {
      // Optionally create an inline policy from selected permissions
      let inlinePolicyId = null;
      if (Array.isArray(groupFormData.selectedPermissionActions) && groupFormData.selectedPermissionActions.length > 0) {
        try {
          const policyRes = await createPolicy({
            name: `Group:${groupFormData.name}:InlinePermissions`,
            description: `Auto-generated inline policy for group ${groupFormData.name}`,
            managed: false,
            statements: [{ effect: 'Allow', actions: groupFormData.selectedPermissionActions, resources: ['*'] }]
          });
          inlinePolicyId = policyRes?.data?.policy?._id || policyRes?.data?.data?.policy?._id || policyRes?.data?.id || policyRes?.id || null;
        } catch (e) {
          console.warn('Failed to create inline policy for group', e);
        }
      }

      const payload = {
        name: groupFormData.name.trim(),
        description: groupFormData.description || '',
        policyIds: Array.from(new Set([...(groupFormData.selectedPolicyIds || []), ...(inlinePolicyId ? [inlinePolicyId] : [])]))
      };

      await createGroup(payload);
      await fetchGroups(groupsPagination.page, groupsPagination.limit);
      setError(null);
      closeGroupModals();
    } catch (err) {
      console.error('Failed to create group:', err);
      setError(err.message || 'Failed to create group');
    } finally {
      setGroupActionLoading(false);
    }
  };

  // Edit Group handler
  const handleEditGroup = async () => {
    if (!selectedGroup?._id) {
      setError('No group selected for update');
      return;
    }

    if (!groupFormData.name.trim()) {
      setError('Group name is required');
      return;
    }

    setGroupActionLoading(true);
    try {
      // Optionally create or update an inline policy from selected permissions
      let inlinePolicyId = null;
      if (Array.isArray(groupFormData.selectedPermissionActions) && groupFormData.selectedPermissionActions.length > 0) {
        try {
          const policyRes = await createPolicy({
            name: `Group:${selectedGroup._id}:InlinePermissions`,
            description: `Auto-generated inline policy for group ${groupFormData.name}`,
            managed: false,
            statements: [{ effect: 'Allow', actions: groupFormData.selectedPermissionActions, resources: ['*'] }]
          });
          inlinePolicyId = policyRes?.data?.policy?._id || policyRes?.data?.data?.policy?._id || policyRes?.data?.id || policyRes?.id || null;
        } catch (e) {
          console.warn('Failed to create inline policy for group', e);
        }
      }

      const payload = {
        name: groupFormData.name.trim(),
        description: groupFormData.description || '',
        policyIds: Array.from(new Set([...(groupFormData.selectedPolicyIds || []), ...(inlinePolicyId ? [inlinePolicyId] : [])]))
      };

      await updateGroup(selectedGroup._id, payload);
      await fetchGroups(groupsPagination.page, groupsPagination.limit);
      setError(null);
      closeGroupModals();
    } catch (err) {
      console.error('Failed to update group:', err);
      setError(err.message || 'Failed to update group');
    } finally {
      setGroupActionLoading(false);
    }
  };

  const handleDeleteGroup = async (group) => {
    if (!confirm(`Are you sure you want to delete the group "${group.name}"? This action cannot be undone.`)) {
      return;
    }

    setGroupActionLoading(true);
    try {
      const response = await deleteGroup(group._id);
      console.log('Group deleted successfully:', response);
      // Refresh groups with current pagination, go to page 1 if current page is empty
      const newPage = groups.length === 1 && groupsPagination.page > 1
        ? groupsPagination.page - 1
        : groupsPagination.page;
      await fetchGroups(newPage, groupsPagination.limit);
      setError(null);
    } catch (err) {
      console.error('Failed to delete group:', err);
      setError(err.message || 'Failed to delete group');
    } finally {
      setGroupActionLoading(false);
    }
  };

  const handleViewGroup = async (group) => {
    setGroupActionLoading(true);
    try {
      const response = await getGroupById(group._id);
      console.log('Group details fetched:', response);
      setViewGroupData(response.data?.data?.group || group);
      setIsViewGroupModalOpen(true);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch group details:', err);
      // Fallback to basic group data if API fails
      setViewGroupData(group);
      setIsViewGroupModalOpen(true);
    } finally {
      setGroupActionLoading(false);
    }
  };

  const openCreateGroupModal = () => {
    setGroupFormData({ name: '', description: '', selectedPolicyIds: [], selectedPermissionActions: [] });
    setIsCreateGroupModalOpen(true);
    setError(null);
  };

  const openEditGroupModal = (group) => {
    setSelectedGroup(group);
    setGroupFormData({
      name: group.name,
      description: group.description || '',
      selectedPolicyIds: Array.isArray(group.policyIds)
        ? group.policyIds.map(p => (typeof p === 'object' ? p._id : p))
        : [],
      selectedPermissionActions: []
    });
    setIsEditGroupModalOpen(true);
    setError(null);
  };

  const closeGroupModals = () => {
    setIsCreateGroupModalOpen(false);
    setIsEditGroupModalOpen(false);
    setIsViewGroupModalOpen(false);
    setSelectedGroup(null);
    setViewGroupData(null);
    setGroupFormData({ name: '', description: '', selectedPolicyIds: [], selectedPermissionActions: [] });
    setError(null);
  };

  // Create user handler
  const handleCreateUserSuccess = async () => {
    // Refresh users list after successful creation
    try {
      const refreshed = await getAllUsers({ limit: 100 });
      setUsers(refreshed.data?.users || []);
    } catch (e) {
      console.warn('Failed to refresh users after creation', e);
    }
  };

  // Edit user handlers
  const openEditUserModal = async (user) => {
    setSelectedUser(user);
    setEditUserFormData({
      email: user.email,
      name: user.name,
      role: user.role,
      status: user.status,
      selectedGroups: Array.isArray(user.groupIds) ? user.groupIds.map(g => typeof g === 'object' ? g._id : g) : [],
      selectedPermissions: Array.isArray(user.permissions) ? user.permissions.map(p => typeof p === 'object' ? p._id : p) : []
    });
    setIsEditUserModalOpen(true);
    setEditUserError(null);
  };

  const closeEditUserModal = () => {
    setIsEditUserModalOpen(false);
    setSelectedUser(null);
    setEditUserFormData({
      email: '',
      name: '',
      role: 'Admin',
      status: 'active',
      selectedGroups: [],
      selectedPermissions: []
    });
    setEditUserError(null);
  };

  const handleEditUserGroupToggle = (groupId) => {
    setEditUserFormData(prev => ({
      ...prev,
      selectedGroups: prev.selectedGroups.includes(groupId)
        ? prev.selectedGroups.filter(id => id !== groupId)
        : [...prev.selectedGroups, groupId]
    }));
  };

  const handleEditUserPermissionToggle = (permissionId) => {
    setEditUserFormData(prev => ({
      ...prev,
      selectedPermissions: prev.selectedPermissions.includes(permissionId)
        ? prev.selectedPermissions.filter(id => id !== permissionId)
        : [...prev.selectedPermissions, permissionId]
    }));
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    setEditUserError(null);

    if (!editUserFormData.email.trim() || !editUserFormData.name.trim()) {
      setEditUserError('Email and name are required');
      return;
    }

    setUserActionLoading(true);
    try {
      const payload = {
        email: editUserFormData.email,
        name: editUserFormData.name,
        role: editUserFormData.role,
        status: editUserFormData.status,
        groups: editUserFormData.selectedGroups,
        permissions: editUserFormData.selectedPermissions
      };

      // Use _id if available, otherwise fallback to id
      const userId = selectedUser._id || selectedUser.id;
      if (!userId) {
        throw new Error('User ID not found');
      }

      const response = await updateUser(userId, payload);
      console.log('User updated successfully:', response);

      closeEditUserModal();

      // Refresh users list
      try {
        const refreshed = await getAllUsers({ limit: 100 });
        setUsers(refreshed.data?.users || []);
      } catch (e) {
        console.warn('Failed to refresh users after update', e);
      }
    } catch (err) {
      console.error('Failed to update user:', err);
      setEditUserError(err.message || 'Failed to update user');
    } finally {
      setUserActionLoading(false);
    }
  };

  // Toggle user status handler
  const handleToggleUserStatus = async (user) => {
    const newStatus = user.status === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'activate' : 'deactivate';

    if (!confirm(`Are you sure you want to ${action} the user "${user.name || user.email}"?`)) {
      return;
    }

    setUserActionLoading(true);
    try {
      // Use _id if available, otherwise fallback to id
      const userId = user._id || user.id;
      if (!userId) {
        throw new Error('User ID not found');
      }

      // Update user status using the existing updateUser function
      const payload = {
        email: user.email,
        name: user.name,
        role: user.role,
        status: newStatus,
        groups: Array.isArray(user.groupIds) ? user.groupIds.map(g => typeof g === 'object' ? g._id : g) : [],
        permissions: Array.isArray(user.permissions) ? user.permissions.map(p => typeof p === 'object' ? p._id : p) : []
      };

      const response = await updateUser(userId, payload);
      console.log(`User ${action}d successfully:`, response);

      // Refresh users list
      try {
        const refreshed = await getAllUsers({ limit: 100 });
        setUsers(refreshed.data?.users || []);
      } catch (e) {
        console.warn(`Failed to refresh users after ${action}`, e);
      }

      setError(null);
    } catch (err) {
      console.error(`Failed to ${action} user:`, err);
      setError(err.message || `Failed to ${action} user`);
    } finally {
      setUserActionLoading(false);
    }
  };  // Permissions popup handlers
  const handleShowAllPermissions = (user) => {
    const userName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email;
    setPopupTitle(`All Permissions for ${userName}`);
    // Show full permission catalog (149) instead of only assigned
    setPopupPermissions(Array.isArray(allPermissions) ? allPermissions : []);
    setIsPermissionsPopupOpen(true);
  };

  const closePermissionsPopup = () => {
    setIsPermissionsPopupOpen(false);
    setPopupPermissions([]);
    setPopupTitle('');
  };

  // View user profile handler
  const handleViewUserProfile = async (user) => {
    setIsViewUserProfileOpen(true);
    setViewUserProfileData(null);
    setViewUserProfileLoading(true);
    try {
      const res = await getUserProfileById(user._id || user.id);
      if (res?.data?.data) {
        setViewUserProfileData(res.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
    } finally {
      setViewUserProfileLoading(false);
    }
  };

  const closeViewUserProfile = () => {
    setIsViewUserProfileOpen(false);
    setViewUserProfileData(null);
  };

  // Activity detail modal state
  const [activityDetailLog, setActivityDetailLog] = useState(null);

  // Clear Logs modal state
  const [showClearLogsModal, setShowClearLogsModal] = useState(false);
  const [clearLogsMode, setClearLogsMode] = useState('preset'); // 'preset' | 'custom'
  const [clearLogsDays, setClearLogsDays] = useState(30);
  const [clearLogsDateFrom, setClearLogsDateFrom] = useState('');
  const [clearLogsDateTo, setClearLogsDateTo] = useState('');
  const [clearLogsLoading, setClearLogsLoading] = useState(false);
  const [clearLogsResult, setClearLogsResult] = useState(null); // { deletedCount }

  // Toggle user name expansion
  const toggleUserNameExpansion = (userId) => {
    setExpandedUserNames(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Helper function to truncate text
  const truncateText = (text, maxLength = 40) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength);
  };

  // Update stats when data changes
  useEffect(() => {
    setStats({
      totalUsers: Array.isArray(users) ? users.length : 0,
      activeUsers: Array.isArray(users) ? users.filter(u => u.status === 'active').length : 0,
      totalGroups: Array.isArray(groups) ? groups.length : 0,
      totalPermissions: Array.isArray(allPermissions) ? allPermissions.length : 0,
      totalPolicies: Array.isArray(allPolicies) ? allPolicies.length : 0,
      totalActivityLogs: activityLogsPagination.total || 0
    });
  }, [users, allPermissions, allPolicies, groups, activityLogsPagination.total]);

  // Debug permissions state changes
  useEffect(() => {
    console.log('Permissions state updated:', permissions);
  }, [permissions]);

  // Load data on component mount
  useEffect(() => {
    console.log('🚀 AccessManagement component mounted, loading initial data...');
    fetchAllData();
  }, []);

  // Debug effect to monitor filter changes
  useEffect(() => {
    console.log('🔧 Activity filters changed:', activityFilters);
  }, [activityFilters]);

  // Debug effect to monitor pagination changes  
  useEffect(() => {
    console.log('📖 Activity logs pagination changed:', activityLogsPagination);
  }, [activityLogsPagination]);

  // Debug effect to monitor activity logs data changes
  useEffect(() => {
    console.log('📊 Activity logs data changed:', {
      count: activityLogs.length,
      data: activityLogs.slice(0, 2) // Show first 2 items for debugging
    });
  }, [activityLogs]);

  const tabs = [
    { id: 'overview', name: 'Overview', icon: Shield },
    { id: 'users', name: 'Users', icon: Users },
    { id: 'groups', name: 'Groups', icon: Users },
    { id: 'permissions', name: 'Permissions', icon: Key }
  ];

  const INP = 'w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-semibold text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#9DC9D4] focus:border-[#2D869C] hover:border-[#2D869C] transition-all bg-white';
  const INP_RO = 'w-full border-2 border-gray-200 rounded-lg px-4 py-2.5 text-md font-semibold text-gray-400 focus:outline-none bg-gray-50 cursor-not-allowed';
  const LBL = 'block text-md font-semibold text-gray-600 mb-2 uppercase tracking-wide';

  // ─── Activity Log Display Helpers ────────────────────────────────────────────
  const ACTIVITY_TYPE_LABELS = {
    login: 'User Login',
    logout: 'User Logout',
    password_change: 'Password Changed',
    password_reset: 'Password Reset',
    create: 'Record Created',
    read: 'Record Viewed',
    update: 'Record Updated',
    delete: 'Record Deleted',
    user_create: 'User Account Created',
    user_update: 'User Account Updated',
    user_delete: 'User Account Deleted',
    user_activate: 'User Account Activated',
    user_deactivate: 'User Account Deactivated',
    permission_change: 'Permission Changed',
    role_change: 'Role Changed',
    appointment_create: 'Appointment Scheduled',
    appointment_update: 'Appointment Updated',
    appointment_cancel: 'Appointment Cancelled',
    patient_create: 'New Patient Registered',
    patient_update: 'Patient Record Updated',
    prescription_create: 'Prescription Issued',
    prescription_update: 'Prescription Updated',
    lab_report_create: 'Lab Report Created',
    lab_report_update: 'Lab Report Updated',
    emr_create: 'Medical Record Created',
    emr_update: 'Medical Record Updated',
    emr_delete: 'Medical Record Deleted',
    emr_read: 'Medical Record Viewed',
    billing_create: 'Billing Record Created',
    billing_update: 'Billing Record Updated',
    billing_delete: 'Billing Record Deleted',
    invoice_create: 'Invoice Created',
    invoice_update: 'Invoice Updated',
    invoice_cancel: 'Invoice Cancelled',
    payment_create: 'Payment Recorded',
    payment_update: 'Payment Updated',
    export: 'Data Exported',
    import: 'Data Imported',
    config_change: 'Settings Changed',
    availability_expire: 'Availability Slots Expired',
  };

  const RESOURCE_DISPLAY_LABELS = {
    patients: 'Patients',
    appointments: 'Appointments',
    users: 'Users',
    admin_users: 'Users',
    admin: 'Admin',
    prescriptions: 'Prescriptions',
    lab_reports: 'Lab Reports',
    billing: 'Billing',
    invoices: 'Invoices',
    payments: 'Payments',
    inventory: 'Inventory',
    medicines: 'Medicines',
    imaging_master: 'Imaging Services',
    imaging: 'Imaging',
    emr: 'Medical Records',
    immunizations: 'Immunizations',
    immunization: 'Immunizations',
    beds: 'Beds',
    hr: 'HR',
    payroll: 'Payroll',
    hospital_settings: 'Hospital Settings',
    settings: 'Settings',
    auth: 'Authentication',
    activity_logs: 'Activity Logs',
    reports: 'Reports',
    availability: 'Availability',
    family: 'Family Records',
    charges: 'Charges',
    claim_form_a: 'Insurance Claim (Form A)',
    claim_form_b: 'Insurance Claim (Form B)',
    slogans: 'Slogans',
    master_data: 'Master Data',
    complete_service: 'Service Completion',
  };

  const MONGO_ID_RE = /^[0-9a-f]{24}$/i;
  const RAW_HTTP_RE = /^(GET|POST|PUT|PATCH|DELETE)\s+\//i;

  const METHOD_VERBS = { GET: 'Viewed', POST: 'Created', PUT: 'Updated', PATCH: 'Updated', DELETE: 'Deleted' };

  const humanizeActivityType = (type) =>
    ACTIVITY_TYPE_LABELS[type] || (type ? type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—');

  const humanizeAction = (action, activityType, resource) => {
    if (!action || RAW_HTTP_RE.test(action)) {
      // Derive from activityType label or fallback
      const typeLabel = ACTIVITY_TYPE_LABELS[activityType];
      if (typeLabel) return typeLabel;
      const res = RESOURCE_DISPLAY_LABELS[resource] || (resource || '').replace(/_/g, ' ');
      const verb = activityType ? activityType.split('_')[0] : 'Updated';
      return res ? `${verb.charAt(0).toUpperCase() + verb.slice(1)} ${res}` : 'Action performed';
    }
    return action;
  };

  const humanizeResource = (resource) => {
    if (!resource || resource === '—') return '—';
    const cleaned = resource.replace(/[0-9a-f]{24}_?/gi, '').replace(/^_|_$/g, '').replace(/_/g, ' ').trim();
    if (!cleaned) return '—';
    const key = resource.replace(/-/g, '_');
    return RESOURCE_DISPLAY_LABELS[key] || RESOURCE_DISPLAY_LABELS[cleaned.replace(/ /g, '_')] ||
      cleaned.replace(/\b\w/g, c => c.toUpperCase());
  };

  // Derive a specific, contextual human-readable summary from the full log object
  const getActivitySummary = (log) => {
    const d = log.newData || {};
    const action = log.action || '';
    const type = log.activityType || '';
    const inr = (n) => (n !== undefined && n !== null) ? `₹${Number(n).toLocaleString('en-IN')}` : null;

    // Payment
    if (type === 'payment_create' || action.includes('/payments')) {
      const method = d.paymentMethod || 'Cash';
      const amt = inr(d.amount);
      return amt ? `Recorded ${method} payment of ${amt}` : 'Payment recorded';
    }
    // Invoice / Billing create (has billingType or medicines array)
    if (type === 'invoice_create' || (type === 'create' && (d.billingType !== undefined || (d.medicines && !d.items)))) {
      const parts = [];
      if (d.medicines?.length)    parts.push(`${d.medicines.length} medicine${d.medicines.length > 1 ? 's' : ''}`);
      if (d.tests?.length)        parts.push(`${d.tests.length} lab test${d.tests.length > 1 ? 's' : ''}`);
      if (d.imagingTests?.length) parts.push(`${d.imagingTests.length} imaging test${d.imagingTests.length > 1 ? 's' : ''}`);
      if (d.services?.length)     parts.push(`${d.services.length} service${d.services.length > 1 ? 's' : ''}`);
      return parts.length ? `Created invoice — ${parts.join(', ')}` : 'Invoice created';
    }
    // Prescription create (has items + doctor + patient)
    if (type === 'prescription_create' || (type === 'create' && d.items !== undefined && d.patient)) {
      const parts = [];
      if (d.items?.length)        parts.push(`${d.items.length} medicine${d.items.length > 1 ? 's' : ''}`);
      if (d.tests?.length)        parts.push(`${d.tests.length} lab test${d.tests.length > 1 ? 's' : ''}`);
      if (d.imagingTests?.length) parts.push(`${d.imagingTests.length} imaging test${d.imagingTests.length > 1 ? 's' : ''}`);
      return parts.length ? `Issued prescription — ${parts.join(', ')}` : 'Prescription issued';
    }
    // Appointment booked
    if (action.includes('book-appointment') || log.resource === 'book_appointment') {
      return d.complaint ? `Appointment booked — "${d.complaint}"` : 'Appointment booked';
    }
    // Join queue
    if (action.includes('join-queue') || (log.resource || '').includes('join_queue')) {
      return 'Patient added to doctor queue';
    }
    // Complete service
    if (action.includes('complete-service') || (log.resource || '').includes('complete_service')) {
      return 'Appointment service marked as completed';
    }
    // Start service
    if (action.includes('start-service') || (log.resource || '').includes('start_service')) {
      return 'Appointment service started';
    }
    // Slogan update
    if (log.resource === 'slogans' || action.includes('/slogans')) {
      return d.slogan ? `Updated slogan: "${d.slogan}"` : 'Slogan updated';
    }
    // Patient update — name fields present
    if ((type === 'update' || type === 'patient_update') && (d.firstName || d.lastName)) {
      const name = [d.firstName, d.lastName].filter(Boolean).join(' ');
      return `Updated patient record for ${name}`;
    }
    // Patient create
    if (type === 'patient_create' && (d.firstName || d.lastName)) {
      return `Registered new patient: ${[d.firstName, d.lastName].filter(Boolean).join(' ')}`;
    }
    // User account events
    if (['user_create','user_update','user_delete','user_activate','user_deactivate'].includes(type)) {
      const name = d.name || d.email || '';
      return `${ACTIVITY_TYPE_LABELS[type] || type}${name ? ` — ${name}` : ''}`;
    }
    // Auth
    if (type === 'login')           return 'Signed in to the system';
    if (type === 'logout')          return 'Signed out of the system';
    if (type === 'password_change') return 'Password was changed';
    // Generic with data
    if ((type === 'update' || type === 'create') && Object.keys(d).length > 0) {
      const keys = Object.keys(d).slice(0, 3).map(k => k.replace(/([A-Z])/g, ' $1').toLowerCase()).join(', ');
      return `${type === 'update' ? 'Updated' : 'Created'} — ${keys}`;
    }
    return humanizeActivityType(type);
  };
  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <div className="bg-white rounded-xl border-2 border-gray-200 shadow-sm" style={{ fontFamily: 'Poppins' }}>
      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center p-8">
          <Loader2 className="w-8 h-8 animate-spin text-[#2D869C] mr-3" />
          <span className="text-lg font-medium text-gray-600">Loading access management data...</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="p-6 bg-red-50 border-l-4 border-red-400 rounded-lg">
          <div className="flex items-center">
            <AlertTriangle className="w-6 h-6 text-red-400 mr-3" />
            <div className="text-lg font-medium text-red-800">{error}</div>
            <button
              onClick={fetchAllData}
              className="ml-auto px-4 py-2 bg-red-100 text-red-800 text-lg font-semibold rounded-lg hover:bg-red-200 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      {!loading && (
        <div>
          {/* Header */}
          <div className="p-6 border-b-2 border-gray-200">
            {/* Tab Navigation - Pills Style like Doctor Dashboard */}
            <div className="flex items-center justify-center">
              <nav className="flex items-center bg-gray-100 rounded-full p-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`
                        px-4 py-2 text-lg font-medium transition-all duration-200 rounded-full whitespace-nowrap flex items-center space-x-2
                        ${isActive
                          ? 'bg-[#2D869C] text-white shadow-sm'
                          : 'text-gray-600 hover:text-[#2D869C] hover:bg-gray-200'
                        }
                      `}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{tab.name}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && (
              <div className="space-y-6">
                {/* Stats Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-5">
                  <div className="bg-[#2D869C]/10 rounded-xl p-5">
                    <div className="flex items-center">
                      <Users className="w-10 h-10 text-[#2D869C]" />
                      <div className="ml-4">
                        <p className="text-lg font-semibold text-[#2D869C]">Total Users</p>
                        <p className="text-3xl font-bold text-gray-900">{stats.totalUsers}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-xl p-5">
                    <div className="flex items-center">
                      <Users className="w-10 h-10 text-green-600" />
                      <div className="ml-4">
                        <p className="text-lg font-semibold text-green-600">Active Users</p>
                        <p className="text-3xl font-bold text-green-900">{stats.activeUsers}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-5">
                    <div className="flex items-center">
                      <Shield className="w-10 h-10 text-purple-600" />
                      <div className="ml-4">
                        <p className="text-lg font-semibold text-purple-600">Groups</p>
                        <p className="text-3xl font-bold text-purple-900">{stats.totalGroups}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-orange-50 rounded-xl p-5">
                    <div className="flex items-center">
                      <Key className="w-10 h-10 text-orange-600" />
                      <div className="ml-4">
                        <p className="text-lg font-semibold text-orange-600">Permissions</p>
                        <p className="text-3xl font-bold text-orange-900">{stats.totalPermissions}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-red-50 rounded-xl p-5">
                    <div className="flex items-center">
                      <Shield className="w-10 h-10 text-red-600" />
                      <div className="ml-4">
                        <p className="text-lg font-semibold text-red-600">Policies</p>
                        <p className="text-3xl font-bold text-red-900">{stats.totalPolicies}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-indigo-50 rounded-xl p-5">
                    <div className="flex items-center">
                      <Activity className="w-10 h-10 text-indigo-600" />
                      <div className="ml-4">
                        <p className="text-lg font-semibold text-indigo-600">Activity Logs</p>
                        <p className="text-3xl font-bold text-indigo-900">{stats.totalActivityLogs}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Activity Logs Preview */}
                <div className="bg-white rounded-xl shadow-sm border-2 border-gray-200 px-6 pt-6 w-full">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-2xl font-bold text-gray-900">Recent Activity Logs</h3>
                      <div className="text-sm text-gray-500">
                        Last updated: {new Date().toLocaleTimeString()}
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => setShowActivityFilters(!showActivityFilters)}
                        className="flex items-center gap-2 px-4 py-2 text-md font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 transition-all"
                      >
                        <Filter className="w-5 h-5" />
                        Filters
                        {Object.values(activityFilters).some(v => v && v !== 'timestamp' && v !== 'desc') && (
                          <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded-full text-sm font-bold bg-[#9DC9D4] text-[#2D869C]">
                            Active
                          </span>
                        )}
                      </button>
                      <button
                        onClick={handleExportActivityLogs}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 text-md font-bold text-green-700 bg-white border-2 border-green-200 rounded-xl hover:border-green-400 hover:bg-green-50 disabled:opacity-50 transition-all"
                      >
                        <FileSpreadsheet className="w-5 h-5" />
                        Export Excel
                      </button>
                      <button
                        onClick={() => { setClearLogsMode('preset'); setClearLogsDays(30); setClearLogsDateFrom(''); setClearLogsDateTo(''); setClearLogsResult(null); setShowClearLogsModal(true); }}
                        className="flex items-center gap-2 px-4 py-2 text-md font-bold text-red-600 bg-white border-2 border-red-200 rounded-xl hover:border-red-400 hover:bg-red-50 transition-all"
                      >
                        <Trash2 className="w-5 h-5" />
                        Clear Logs
                      </button>
                      <button
                        onClick={() => fetchActivityLogs(1, activityLogsPagination.limit, activityFilters)}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 text-md font-bold text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 disabled:opacity-50 transition-all"
                      >
                        <RotateCcw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                        Refresh
                      </button>
                    </div>
                  </div>

                  {/* Pagination - Always show if we have pages or are loading */}
                  {(activityLogsPagination.pages > 1 || activityLogsPagination.total > 0) && (
                    <div className="flex justify-center mb-4">
                      <Pagination
                        currentPage={activityLogsPagination.page || 1}
                        totalPages={activityLogsPagination.pages || 1}
                        onPageChange={handleActivityLogsPageChange}
                        showSummary={true}
                        totalItems={activityLogsPagination.total || 0}
                        itemsPerPage={activityLogsPagination.limit || 10}
                      />
                    </div>
                  )}

                  {/* Results Info */}
                  <div className="flex justify-between items-center mb-4">


                  </div>
                </div>

                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={activityFilters.search}
                      onChange={(e) => {
                        handleActivityFilterChange('search', e.target.value);
                        // Debounced search - search as user types
                        clearTimeout(window.activitySearchTimeout);
                        window.activitySearchTimeout = setTimeout(() => {
                          handleActivitySearch(e.target.value);
                        }, 500);
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && handleActivityFiltersApply()}
                      placeholder="Search in activity descriptions..."
                      className={`${INP} pl-10`}
                    />
                    {activityFilters.search && (
                      <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                        <button
                          onClick={() => {
                            handleActivityFilterChange('search', '');
                            handleActivityFiltersApply();
                          }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X className="h-5 w-5" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Advanced Filters Panel */}
                {showActivityFilters && (
                  <div className="mb-4 p-5 bg-white rounded-xl border-2 border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {/* User Email Filter */}
                      <div>
                        <label className={LBL}>User Email</label>
                        <input
                          type="email"
                          value={activityFilters.userEmail}
                          onChange={(e) => handleActivityFilterChange('userEmail', e.target.value)}
                          placeholder="Filter by user email..."
                          className={INP}
                        />
                      </div>

                      {/* User Role Filter */}
                      <div>
                        <label className={LBL}>User Role</label>
                        <select
                          value={activityFilters.userRole}
                          onChange={(e) => handleActivityFilterChange('userRole', e.target.value)}
                          className={INP}
                        >
                          <option value="">All Roles</option>
                          {activityFilterOptions.userRoles.map(role => (
                            <option key={role} value={role}>{role}</option>
                          ))}
                        </select>
                      </div>

                      {/* Activity Type Filter */}
                      <div>
                        <label className={LBL}>Activity Type</label>
                        <select
                          value={activityFilters.activityType}
                          onChange={(e) => handleActivityFilterChange('activityType', e.target.value)}
                          className={INP}
                        >
                          <option value="">All Activity Types</option>
                          {activityFilterOptions.activityTypes.map(type => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>

                      {/* Resource Filter */}
                      <div>
                        <label className={LBL}>Resource</label>
                        <select
                          value={activityFilters.resource}
                          onChange={(e) => handleActivityFilterChange('resource', e.target.value)}
                          className={INP}
                        >
                          <option value="">All Resources</option>
                          {activityFilterOptions.resources.map(resource => (
                            <option key={resource} value={resource}>{resource}</option>
                          ))}
                        </select>
                      </div>

                      {/* Success Status Filter */}
                      <div>
                        <label className={LBL}>Status</label>
                        <select
                          value={activityFilters.success}
                          onChange={(e) => handleActivityFilterChange('success', e.target.value)}
                          className={INP}
                        >
                          <option value="">All Status</option>
                          <option value="true">Success</option>
                          <option value="false">Failed</option>
                        </select>
                      </div>

                      {/* Sort Order */}
                      <div>
                        <label className={LBL}>Sort Order</label>
                        <select
                          value={activityFilters.sortOrder}
                          onChange={(e) => handleActivityFilterChange('sortOrder', e.target.value)}
                          className={INP}
                        >
                          <option value="desc">Newest First</option>
                          <option value="asc">Oldest First</option>
                        </select>
                      </div>

                      {/* Date From */}
                      <div>
                        <label className={LBL}>From Date</label>
                        <input
                          type="datetime-local"
                          value={activityFilters.dateFrom}
                          onChange={(e) => handleActivityFilterChange('dateFrom', e.target.value)}
                          className={INP}
                        />
                      </div>

                      {/* Date To */}
                      <div>
                        <label className={LBL}>To Date</label>
                        <input
                          type="datetime-local"
                          value={activityFilters.dateTo}
                          onChange={(e) => handleActivityFilterChange('dateTo', e.target.value)}
                          className={INP}
                        />
                      </div>
                    </div>

                    {/* Active Filters Display */}
                    {Object.values(activityFilters).some(v => v && v !== 'timestamp' && v !== 'desc') && (
                      <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-sm font-medium text-blue-800 mb-2">Active Filters:</div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(activityFilters).map(([key, value]) => {
                            if (!value || value === 'timestamp' || value === 'desc') return null;
                            return (
                              <span key={key} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {key}: {value}
                                <button
                                  onClick={() => {
                                    handleActivityFilterChange(key, '');
                                  }}
                                  className="ml-1 text-[#2D869C] hover:text-[#247180]"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Filter Actions */}
                    <div className="flex justify-between items-center mt-4 pt-4 border-t border-gray-200">
                      <div className="text-md font-semibold text-gray-500">
                        {activityLogsPagination.total > 0 && (
                          `Showing ${((activityLogsPagination.page - 1) * activityLogsPagination.limit) + 1}-${Math.min(activityLogsPagination.page * activityLogsPagination.limit, activityLogsPagination.total)} of ${activityLogsPagination.total} results`
                        )}
                        {loading && <span className="ml-2 text-[#2D869C]">Loading...</span>}
                      </div>
                      <div className="flex space-x-3">
                        <button
                          onClick={handleActivityFiltersReset}
                          disabled={loading}
                          className="flex items-center gap-2 px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 font-bold text-md transition-all disabled:opacity-50"
                        >
                          {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RotateCcw className="w-4 h-4" />
                          )}
                          Reset
                        </button>
                        <button
                          onClick={handleActivityFiltersApply}
                          disabled={loading}
                          className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white rounded-xl font-bold text-md shadow-lg hover:shadow-xl transition-all disabled:opacity-50"
                        >
                          {loading ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Search className="w-4 h-4" />
                          )}
                          Apply Filters
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {Array.isArray(activityLogs) && activityLogs.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-[#2D869C] to-[#2D869C]/90">
                        <tr>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">User</th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">Activity</th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">Resource</th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">Status</th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">Time</th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">Details</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {activityLogs.map((log) => (
                          <tr key={log._id} className="hover:bg-gray-50">
                            {/* User */}
                            <td className="px-6 py-4">
                              <div className="flex items-center">
                                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                  <Users className="w-5 h-5 text-[#2D869C]" />
                                </div>
                                <div className="ml-3">
                                  <div className="text-md font-semibold text-gray-900">
                                    {log.userName || "Unknown User"}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {log.userEmail}
                                  </div>
                                </div>
                              </div>
                            </td>

                            {/* Activity */}
                            <td className="px-6 py-4">
                              <div className="text-md font-semibold text-gray-900">{humanizeActivityType(log.activityType)}</div>
                              <div className="text-sm text-gray-500">{getActivitySummary(log)}</div>
                            </td>

                            {/* Resource */}
                            <td className="px-6 py-4 text-md font-semibold text-gray-700">
                              {humanizeResource(log.resource)}
                            </td>

                            {/* Status */}
                            <td className="px-6 py-4">
                              <span className={`inline-flex px-3 py-1.5 text-md font-semibold rounded-lg ${log.success
                                ? 'bg-[#A4EC90]/20 text-green-800'
                                : 'bg-[#E99797]/20 text-red-800'
                                }`}>
                                {log.success ? 'Success' : 'Failed'}
                              </span>
                            </td>

                            {/* Time */}
                            <td className="px-6 py-4 text-md font-semibold text-gray-700">
                              {log.timestamp ? (
                                <div>
                                  <div>{new Date(log.timestamp).toLocaleDateString()}</div>
                                  <div className="text-md font-semibold text-gray-500">{new Date(log.timestamp).toLocaleTimeString()}</div>
                                </div>
                              ) : "—"}
                            </td>
                            {/* View Details */}
                            <td className="px-6 py-4">
                              <button
                                onClick={() => setActivityDetailLog(log)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-bold text-[#2D869C] border-2 border-[#2D869C] rounded-lg hover:bg-[#2D869C] hover:text-white transition-all"
                              >
                                <Eye className="w-3.5 h-3.5" />
                                View
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-md font-semibold">No activity logs found</p>
                )}

                {/* Activity Logs Pagination */}
                {Array.isArray(activityLogs) && activityLogs.length > 0 && (
                  <div className="px-4 py-3 border-t border-gray-200">
                    <Pagination
                      pagination={activityLogsPagination}
                      onPageChange={(page) => fetchActivityLogs(page, activityLogsPagination.limit, activityFilters)}
                      loading={loading}
                    />
                  </div>
                )}
              </div>

            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
              <div className="space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Users Management</h3>
                    <p className="text-lg font-medium text-gray-500">Found {users.length} users in the system</p>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={() => setIsCreateUserModalOpen(true)}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white rounded-xl font-bold text-md shadow-lg hover:shadow-xl transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create User
                    </button>
                  </div>
                </div>

                {/* Filters Section */}
                <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-xl font-bold text-gray-900">Filters & Search</h4>
                    <button
                      onClick={() => setShowUserFilters(!showUserFilters)}
                      className="inline-flex items-center px-4 py-2 text-lg font-semibold text-gray-700 bg-white border-2 border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <Filter className="w-5 h-5 mr-2" />
                      {showUserFilters ? 'Hide' : 'Show'} Filters
                    </button>
                  </div>

                  {showUserFilters && (
                    <div className="p-5 space-y-5">
                      {/* Search */}
                      <div className="relative">
                        <Search className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                        <input
                          type="text"
                          placeholder="Search by name or email..."
                          value={userSearchTerm}
                          onChange={(e) => setUserSearchTerm(e.target.value)}
                          className={`${INP} pl-10`}
                        />
                      </div>

                      {/* Role and Status Filters */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className={LBL}>Filter by Role</label>
                          <select
                            value={userRoleFilter}
                            onChange={(e) => setUserRoleFilter(e.target.value)}
                            className={INP}
                          >
                            <option value="">All Roles</option>
                            <option value="Admin">Administrator</option>
                            <option value="Doctor">Doctor</option>
                            <option value="Receptionist">Receptionist</option>
                            <option value="Nurse">Nurse</option>
                          </select>
                        </div>

                        <div>
                          <label className={LBL}>Filter by Status</label>
                          <select
                            value={userStatusFilter}
                            onChange={(e) => setUserStatusFilter(e.target.value)}
                            className={INP}
                          >
                            <option value="">All Status</option>
                            <option value="active">Active</option>
                            <option value="inactive">Inactive</option>
                          </select>
                        </div>
                      </div>

                      {/* Filter Actions */}
                      <div className="flex justify-between items-center pt-4 border-t-2 border-gray-200">
                        <div className="text-md font-semibold text-gray-600">
                          Showing {filteredUsers.length} of {users.length} users
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={() => {
                              setUserSearchTerm('');
                              setUserRoleFilter('');
                              setUserStatusFilter('');
                            }}
                            className="flex items-center gap-2 px-5 py-2.5 text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 font-bold text-md transition-all"
                          >
                            <RotateCcw className="w-4 h-4" />
                            Reset
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Users Table */}
                <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  {/* Scroll hint */}

                  {Array.isArray(filteredUsers) && filteredUsers.length > 0 ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gradient-to-r from-[#2D869C] to-[#2D869C]/90">
                        <tr>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider w-1/4">
                            User
                          </th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider w-1/12">
                            Role
                          </th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider w-1/12">
                            Status
                          </th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider w-1/6">
                            Groups
                          </th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider w-1/4">
                            Permissions
                          </th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider w-1/12">
                            Created
                          </th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider w-1/12">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredUsers.map((user) => (
                          <tr key={user._id || user.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <Users className="w-5 h-5 text-blue-600" />
                                  </div>
                                </div>
                                <div className="ml-3 min-w-0 flex-1">
                                  <div className="text-md font-semibold text-gray-900">
                                    {(() => {
                                      const fullName = user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim();
                                      const userId = user._id || user.id;
                                      const isExpanded = expandedUserNames.has(userId);
                                      const shouldTruncate = fullName.length > 40;

                                      if (!shouldTruncate) {
                                        return <span>{fullName}</span>;
                                      }

                                      return (
                                        <div>
                                          {isExpanded ? (
                                            <span classname="text-md font-semibold">
                                              {fullName}{' '}
                                              <button
                                                onClick={() => toggleUserNameExpansion(userId)}
                                                className="text-blue-600 hover:text-blue-800 text-md font-medium"
                                                title="Show less"
                                              >
                                                Show less
                                              </button>
                                            </span>
                                          ) : (
                                            <span>
                                              {truncateText(fullName, 40)}...{' '}
                                              <button
                                                onClick={() => toggleUserNameExpansion(userId)}
                                                className="text-blue-600 hover:text-blue-800 text-md font-medium"
                                                title="Show more"
                                              >
                                                Show more
                                              </button>
                                            </span>
                                          )}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                  <div className="text-md text-gray-500 font-semibold truncate">{user.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-md font-semibold bg-blue-100 text-blue-800">
                                {user.role}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex items-center px-3 py-1.5 rounded-lg text-md font-semibold ${user.status === 'active'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-red-100 text-red-800'
                                }`}>
                                {user.status}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-wrap gap-2">
                                {user.groupIds && user.groupIds.length > 0 ? (
                                  <>
                                    {user.groupIds.slice(0, 2).map((group) => (
                                      <span key={typeof group === 'object' ? group._id : group} className="inline-flex items-center px-3 py-1.5 rounded-lg text-md font-semibold bg-purple-100 text-purple-800">
                                        {typeof group === 'object' ? group.name : group}
                                      </span>
                                    ))}
                                    {user.groupIds.length > 2 && (
                                      <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-md font-semibold bg-gray-100 text-gray-800">
                                        +{user.groupIds.length - 2}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-md text-gray-500 font-semibold">No groups</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-wrap gap-2">
                                {user.permissions && user.permissions.length > 0 ? (
                                  <>
                                    {user.permissions.slice(0, 2).map((permission) => (
                                      <span key={typeof permission === 'object' ? permission._id : permission} className="inline-flex items-center px-3 py-1.5 rounded-lg text-md font-semibold bg-orange-100 text-orange-800">
                                        {typeof permission === 'object' ? permission.name : permission}
                                      </span>
                                    ))}
                                    {user.permissions.length > 2 && (
                                      <button
                                        onClick={() => handleShowAllPermissions(user)}
                                        className="inline-flex items-center px-3 py-1.5 rounded-lg text-md font-semibold bg-gray-100 text-gray-800 hover:bg-gray-200 hover:text-gray-900 transition-colors cursor-pointer"
                                        title="Click to view all permissions"
                                      >
                                        +{user.permissions.length - 2}
                                      </button>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-md text-gray-500 font-semibold">No permissions</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-md font-semibold text-gray-700">
                              {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-md font-semibold text-gray-700 space-x-2">
                              <button
                                onClick={() => handleViewUserProfile(user)}
                                className="text-purple-600 hover:text-purple-800 transition-colors p-1 hover:bg-purple-100 rounded-lg"
                                title="View Profile"
                              >
                                <Eye className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => openEditUserModal(user)}
                                className="text-[#2D869C] hover:text-[#2D869C]/80 transition-colors p-1 hover:bg-[#2D869C]/10 rounded-lg"
                                title="Edit User"
                                disabled={userActionLoading}
                              >
                                <Edit className="w-5 h-5" />
                              </button>
                              <button
                                onClick={() => handleToggleUserStatus(user)}
                                className={`p-1 transition-colors hover:bg-opacity-10 rounded-lg ${user.status === 'active'
                                  ? 'text-red-600 hover:text-red-800 hover:bg-red-100'
                                  : 'text-green-600 hover:text-green-800 hover:bg-green-100'
                                  }`}
                                title={user.status === 'active' ? 'Deactivate User' : 'Activate User'}
                                disabled={userActionLoading}
                              >
                                {user.status === 'active' ? (
                                  <UserX className="w-5 h-5" />
                                ) : (
                                  <UserCheck className="w-5 h-5" />
                                )}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>

                    </table>
                  ) : (
                    <div className="text-center py-12">
                      <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">
                        {users.length === 0 ? 'No users found' : 'No users found matching your filters'}
                      </p>
                      <p className="text-gray-400 text-sm">
                        {users.length === 0 ? 'Users will appear here once they are created' : 'Try adjusting your search or filters'}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Groups Tab */}
            {activeTab === 'groups' && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Groups Management</h3>
                    <p className="text-lg font-medium text-gray-500">
                      {groupsPagination.total > 0
                        ? `Showing ${(groupsPagination.page - 1) * groupsPagination.limit + 1}-${Math.min(groupsPagination.page * groupsPagination.limit, groupsPagination.total)} of ${groupsPagination.total} groups`
                        : `Found ${Array.isArray(groups) ? groups.length : 0} groups in the system`
                      }
                    </p>
                  </div>
                  <div className="flex space-x-3">
                    
                    <button
                      onClick={openCreateGroupModal}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white rounded-xl font-bold text-md shadow-lg hover:shadow-xl transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create Group
                    </button>
                  </div>
                </div>

                {/* Groups Table */}
                <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  {Array.isArray(groups) && groups.length > 0 ? (
                    <table className="min-w-full divide-y-2 divide-gray-200">
                      <thead className="bg-gradient-to-r from-[#2D869C] to-[#2D869C]/90">
                        <tr>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                            Group Name
                          </th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                            Attached Policies
                          </th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                            Members
                          </th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                            Created
                          </th>
                          <th className="relative px-6 py-4">
                            <span className="sr-only">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {groups.map((group) => (
                          <tr key={group._id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                                    <Shield className="w-5 h-5 text-[#2D869C]" />
                                  </div>
                                </div>
                                <div className="ml-3">
                                  <div className="text-md font-semibold text-gray-900">{group.name}</div>
                                  <div className="text-xs text-gray-500">ID: {group._id.slice(-8)}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-md font-semibold text-gray-900">{group.description || 'No description'}</div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex flex-wrap gap-2">
                                {group.policyIds && group.policyIds.length > 0 ? (
                                  <>
                                    {group.policyIds.slice(0, 2).map((policy) => (
                                      <span key={policy._id} className="inline-flex items-center px-3 py-1.5 rounded-lg text-md font-semibold bg-green-100 text-green-800">
                                        {policy.name}
                                      </span>
                                    ))}
                                    {group.policyIds.length > 2 && (
                                      <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-md font-semibold bg-gray-100 text-gray-800">
                                        +{group.policyIds.length - 2} more
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span className="text-md text-gray-500 font-semibold">No policies attached</span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <Users className="w-5 h-5 text-gray-400 mr-1" />
                                <span className="text-md font-semibold text-gray-900">{group.userIds?.length || 0}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-md font-semibold text-gray-700">
                              {new Date(group.createdAt).toLocaleDateString()}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-md font-semibold text-gray-700">
                              <div className="flex justify-end space-x-2">
                                <button
                                  onClick={() => handleViewGroup(group)}
                                  className="text-[#2D869C] hover:text-[#2D869C]/80 transition-colors p-1 hover:bg-[#2D869C]/10 rounded-lg"
                                  title="View Details"
                                  disabled={groupActionLoading}
                                >
                                  <Eye className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => openEditGroupModal(group)}
                                  className="text-[#CCB478] hover:text-[#CCB478]/80 transition-colors p-1 hover:bg-[#CCB478]/10 rounded-lg"
                                  title="Edit Group"
                                  disabled={groupActionLoading}
                                >
                                  <Edit className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => console.log('Manage Policies:', group)}
                                  className="text-purple-600 hover:text-purple-800 transition-colors p-1 hover:bg-purple-100 rounded-lg"
                                  title="Manage Policies"
                                >
                                  <Key className="w-5 h-5" />
                                </button>
                                <button
                                  onClick={() => handleDeleteGroup(group)}
                                  className="text-red-600 hover:text-red-800 transition-colors p-1 hover:bg-red-100 rounded-lg"
                                  title="Delete Group"
                                  disabled={groupActionLoading}
                                >
                                  <Trash2 className="w-5 h-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="text-center py-12">
                      <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No groups configured yet</p>
                      <p className="text-gray-400 text-sm">Create your first group to organize users and permissions</p>
                      <button
                        onClick={openCreateGroupModal}
                        className="mt-4 inline-flex items-center px-4 py-2 bg-[#2D869C] text-white text-sm font-medium rounded-lg hover:bg-[#247180]"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        Create Your First Group
                      </button>
                    </div>
                  )}
                </div>

                {/* Groups Pagination */}
                {Array.isArray(groups) && groups.length > 0 && (
                  <Pagination
                    pagination={groupsPagination}
                    onPageChange={handleGroupsPageChange}
                    loading={loading}
                  />
                )}
              </div>
            )}

            {/* Permissions Tab */}
            {activeTab === 'permissions' && (
              <div className="space-y-6">
                {/* Header */}
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-2xl font-bold text-gray-900">Permissions Management</h3>
                    <p className="text-lg font-medium text-gray-500">
                      {permissionsPagination.total > 0
                        ? `Showing ${(permissionsPagination.page - 1) * permissionsPagination.limit + 1}-${Math.min(permissionsPagination.page * permissionsPagination.limit, permissionsPagination.total)} of ${permissionsPagination.total} permissions`
                        : `Found ${Array.isArray(permissions) ? permissions.length : 0} permissions in the system`
                      }
                    </p>
                  </div>
                  <div className="flex space-x-3">
                    <button
                      onClick={refreshPermissionsCache}
                      className="flex items-center gap-2 px-5 py-2.5 border-2 border-gray-200 rounded-xl text-md font-bold text-gray-700 bg-white hover:border-gray-400 hover:bg-gray-50 transition-all"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      Refresh
                    </button>
                    <button
                      onClick={handleExportPermissions}
                      className="flex items-center gap-2 px-5 py-2.5 border-2 border-green-200 rounded-xl text-md font-bold text-green-700 bg-white hover:border-green-400 hover:bg-green-50 transition-all"
                    >
                      <FileSpreadsheet className="w-5 h-5" />
                      Export Excel
                    </button>
                    <button
                      onClick={() => console.log('Create Permission - Feature coming soon!')}
                      className="flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white rounded-xl font-bold text-md shadow-lg hover:shadow-xl transition-all"
                    >
                      Create Permission
                    </button>
                  </div>
                </div>

                {/* Permissions Table */}
                <div className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden shadow-sm">
                  {Array.isArray(permissions) && permissions.length > 0 ? (
                    <table className="min-w-full divide-y-2 divide-gray-200">
                      <thead className="bg-gradient-to-r from-[#2D869C] to-[#2D869C]/90">
                        <tr>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                            Permission
                          </th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                            Description
                          </th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                            Category
                          </th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                            Usage
                          </th>
                          <th className="px-6 py-4 text-left text-lg font-bold text-white uppercase tracking-wider">
                            Created
                          </th>
                          <th className="relative px-6 py-4">
                            <span className="sr-only">Actions</span>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {permissions.map((permission) => (
                          <tr key={permission._id || permission.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0">
                                  <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                                    <Key className="w-5 h-5 text-orange-600" />
                                  </div>
                                </div>
                                <div className="ml-3">
                                  <div className="text-md font-semibold text-gray-900">{permission.name}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <div className="text-md font-semibold text-gray-900">{permission.description || 'No description'}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex items-center px-3 py-1.5 rounded-lg text-md font-semibold bg-blue-100 text-blue-800">
                                {permission.category || 'General'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-md font-semibold text-gray-900">
                                {permission.usage || 'Not used'}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-md font-semibold text-gray-700">
                              {permission.createdAt ? new Date(permission.createdAt).toLocaleDateString() : 'Unknown'}
                            </td>

                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="text-center py-12">
                      <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No permissions found</p>
                      <p className="text-gray-400 text-sm">Permissions will be loaded from the backend system</p>
                    </div>
                  )}

                  {/* Permissions Pagination */}
                  {Array.isArray(permissions) && permissions.length > 0 && (
                    <Pagination
                      pagination={permissionsPagination}
                      onPageChange={handlePermissionsPageChange}
                      loading={loading}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )
      }

      {/* View Group Modal */}
      {
        isViewGroupModalOpen && viewGroupData && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ fontFamily: 'Poppins' }}>
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4 flex items-center justify-between">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Group Details
                </h3>
                <button onClick={closeGroupModals} className="text-white/80 hover:text-white p-1 hover:bg-white/20 rounded-lg transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div>
                  <label className={LBL}>Group Name</label>
                  <div className={INP_RO}>{viewGroupData.name}</div>
                </div>
                <div>
                  <label className={LBL}>Description</label>
                  <div className={`${INP_RO} min-h-[72px]`}>{viewGroupData.description || 'No description provided'}</div>
                </div>
                <div>
                  <label className={LBL}>Group ID</label>
                  <div className={INP_RO}>{viewGroupData._id}</div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className={LBL}>Members</label>
                    <div className={INP_RO}>{viewGroupData.userIds?.length || 0} users</div>
                  </div>
                  <div>
                    <label className={LBL}>Attached Policies</label>
                    <div className={INP_RO}>{viewGroupData.policyIds?.length || 0} policies</div>
                  </div>
                </div>
                <div>
                  <label className={LBL}>Created At</label>
                  <div className={INP_RO}>{viewGroupData.createdAt ? new Date(viewGroupData.createdAt).toLocaleString() : 'Unknown'}</div>
                </div>

                {/* Attached Policies List */}
                <div>
                  <label className={LBL}>Policy Names</label>
                  <div className="border-2 border-gray-200 rounded-lg p-3 bg-gray-50 min-h-[60px]">
                    {Array.isArray(viewGroupData?.policyIds) && viewGroupData.policyIds.length > 0 ? (
                      <ul className="space-y-1">
                        {viewGroupData.policyIds.map((p) => (
                          <li key={typeof p === 'object' ? p._id : p} className="text-md font-semibold text-gray-600 flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#2D869C] flex-shrink-0" />
                            {typeof p === 'object' ? p.name : p}
                            {typeof p === 'object' && p.description ? (
                              <span className="text-sm font-normal text-gray-400"> — {p.description}</span>
                            ) : null}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="text-md font-semibold text-gray-400">No policies attached</div>
                    )}
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-2 border-t-2 border-gray-200">
                  <button
                    onClick={closeGroupModals}
                    className="flex items-center gap-2 px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 font-bold text-md transition-all"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => { closeGroupModals(); openEditGroupModal(viewGroupData); }}
                    className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] text-white rounded-xl font-bold text-md shadow-lg hover:shadow-xl transition-all"
                  >
                    Edit Group
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }

      {/* Helpers: policy grouping and selection */}
      {
        (() => {
          // Determine group key from policy name
          const getPolicyGroupKey = (name) => {
            if (!name || typeof name !== 'string') return 'Other';
            const normalized = name.trim();
            const delimiters = [':', '.', '-'];
            for (const d of delimiters) {
              if (normalized.includes(d)) {
                const part = normalized.split(d)[0].trim();
                return part || 'Other';
              }
            }
            const first = normalized[0]?.toUpperCase();
            return first ? first : 'Other';
          };

          // Build filtered + grouped policies once per render
          const filtered = Array.isArray(allPolicies)
            ? allPolicies.filter(p => {
              if (!policySearch) return true;
              const hay = `${p.name || ''} ${p.description || ''}`.toLowerCase();
              return hay.includes(policySearch.toLowerCase());
            })
            : [];

          const grouped = filtered.reduce((acc, p) => {
            const key = getPolicyGroupKey(p.name);
            if (!acc[key]) acc[key] = [];
            acc[key].push(p);
            return acc;
          }, {});

          // Attach to window for reuse in modals without re-computing
          window.__policyGroups = grouped;
          window.__filteredPolicyList = filtered;
          return null;
        })()
      }

      {/* Create Group Modal */}
      {
        isCreateGroupModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ fontFamily: 'Poppins' }}>
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
              <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4 flex items-center justify-between flex-shrink-0">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Shield className="w-5 h-5" />
                  Create New Group
                </h3>
                <button onClick={closeGroupModals} className="text-white/80 hover:text-white p-1 hover:bg-white/20 rounded-lg transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="overflow-y-auto p-6 space-y-4 flex-1">
                  <div>
                    <label className={LBL}>Group Name <span className="text-red-500 normal-case">*</span></label>
                    <input
                      type="text"
                      value={groupFormData.name}
                      onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                      className={INP}
                      placeholder="Enter group name"
                    />
                  </div>

                  <div>
                    <label className={LBL}>Description</label>
                    <textarea
                      value={groupFormData.description}
                      onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
                      className={INP}
                      rows="3"
                      placeholder="Enter group description"
                    />
                  </div>

                  <div>
                    <label className={LBL}>Attach Permissions</label>
                    <div className="space-y-2">
                      {/* Search */}
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={popupSearch}
                          onChange={(e) => setPopupSearch(e.target.value)}
                          placeholder="Search permissions by name or description..."
                          className={`${INP} pl-10`}
                        />
                      </div>

                      {/* Bulk actions */}
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500">
                          Total: {Array.isArray(allPermissions) ? allPermissions.length : 0}
                          {' '}• Selected: {groupFormData.selectedPermissionActions.length}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="px-2 py-1 text-xs bg-[#2D869C] text-white rounded hover:bg-[#247180]"
                            onClick={() => {
                              const filtered = Array.isArray(allPermissions) ? allPermissions.filter(p => {
                                if (!popupSearch) return true;
                                const hay = `${p.name || ''} ${p.description || ''}`.toLowerCase();
                                return hay.includes(popupSearch.toLowerCase());
                              }) : [];
                              const actions = filtered.map(p => p.name);
                              setGroupFormData(prev => ({
                                ...prev,
                                selectedPermissionActions: Array.from(new Set([...(prev.selectedPermissionActions || []), ...actions]))
                              }));
                            }}
                          >
                            Select All (filtered)
                          </button>
                          <button
                            type="button"
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200"
                            onClick={() => setGroupFormData(prev => ({ ...prev, selectedPermissionActions: [] }))}
                          >
                            Clear
                          </button>
                        </div>
                      </div>

                      {/* Grouped list */}
                      <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg">
                        {(() => {
                          // Build permissions grouped by category or prefix
                          const source = Array.isArray(allPermissions) ? allPermissions.filter(p => {
                            if (!popupSearch) return true;
                            const hay = `${p.name || ''} ${p.description || ''}`.toLowerCase();
                            return hay.includes(popupSearch.toLowerCase());
                          }) : [];
                          const getKey = (name) => {
                            if (!name) return 'Other';
                            const n = name.trim();
                            const delims = [':', '.', '-'];
                            for (const d of delims) {
                              if (n.includes(d)) return n.split(d)[0];
                            }
                            return (n[0] || 'Other').toUpperCase();
                          };
                          const grouped = source.reduce((acc, p) => {
                            const key = getKey(p.name);
                            if (!acc[key]) acc[key] = [];
                            acc[key].push(p);
                            return acc;
                          }, {});
                          const keys = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
                          if (keys.length === 0) return (<div className="p-2 text-xs text-gray-500">No permissions available</div>);
                          return (
                            <div className="divide-y divide-gray-100">
                              {keys.map((key) => (
                                <div key={key} className="p-2">
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="text-xs font-semibold text-gray-700">{key}</div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                                        onClick={() => {
                                          const actions = (grouped[key] || []).map(p => p.name);
                                          setGroupFormData(prev => ({
                                            ...prev,
                                            selectedPermissionActions: Array.from(new Set([...(prev.selectedPermissionActions || []), ...actions]))
                                          }));
                                        }}
                                      >
                                        Select Group
                                      </button>
                                      <button
                                        type="button"
                                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200"
                                        onClick={() => {
                                          const actions = new Set((grouped[key] || []).map(p => p.name));
                                          setGroupFormData(prev => ({
                                            ...prev,
                                            selectedPermissionActions: (prev.selectedPermissionActions || []).filter(a => !actions.has(a))
                                          }));
                                        }}
                                      >
                                        Clear Group
                                      </button>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 gap-1">
                                    {(grouped[key] || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(perm => (
                                      <label key={perm._id || perm.id} className="flex items-center text-xs py-1">
                                        <input
                                          type="checkbox"
                                          className="mr-2 rounded border-gray-300 text-[#2D869C]"
                                          checked={groupFormData.selectedPermissionActions.includes(perm.name)}
                                          onChange={(e) => {
                                            const checked = e.target.checked;
                                            setGroupFormData(prev => ({
                                              ...prev,
                                              selectedPermissionActions: checked
                                                ? [...prev.selectedPermissionActions, perm.name]
                                                : prev.selectedPermissionActions.filter(a => a !== perm.name)
                                            }));
                                          }}
                                        />
                                        <span className="text-gray-700">
                                          {perm.name}
                                          {perm.description ? (
                                            <span className="text-[10px] text-gray-500"> — {perm.description}</span>
                                          ) : null}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
              </div>

              <div className="flex justify-end gap-3 p-6 border-t-2 border-gray-200 flex-shrink-0">
                  <button
                    onClick={closeGroupModals}
                    className="flex items-center gap-2 px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 font-bold text-md transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateGroup}
                    disabled={groupActionLoading || !groupFormData.name.trim()}
                    className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] disabled:from-gray-400 disabled:to-gray-400 text-white rounded-xl font-bold text-md shadow-lg hover:shadow-xl disabled:cursor-not-allowed transition-all"
                  >
                    {groupActionLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" />Creating...</>
                    ) : (
                      'Create Group'
                    )}
                  </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Edit Group Modal */}
      {
        isEditGroupModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ fontFamily: 'Poppins' }}>
            <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
              <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4 flex items-center justify-between flex-shrink-0">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <Edit className="w-5 h-5" />
                  Edit Group
                </h3>
                <button onClick={closeGroupModals} className="text-white/80 hover:text-white p-1 hover:bg-white/20 rounded-lg transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="overflow-y-auto p-6 space-y-4 flex-1">
                  <div>
                    <label className={LBL}>Group Name <span className="text-red-500 normal-case">*</span></label>
                    <input
                      type="text"
                      value={groupFormData.name}
                      onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                      className={INP}
                      placeholder="Enter group name"
                    />
                  </div>

                  <div>
                    <label className={LBL}>Description</label>
                    <textarea
                      value={groupFormData.description}
                      onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
                      className={INP}
                      rows="3"
                      placeholder="Enter group description"
                    />
                  </div>

                  <div>
                    <label className={LBL}>Attach Policies</label>
                    <div className="space-y-2">
                      {/* Search */}
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Search className="h-4 w-4 text-gray-400" />
                        </div>
                        <input
                          type="text"
                          value={policySearch}
                          onChange={(e) => setPolicySearch(e.target.value)}
                          placeholder="Search policies by name or description..."
                          className={`${INP} pl-10`}
                        />
                      </div>

                      {/* Bulk actions */}
                      <div className="flex items-center justify-between">
                        <div className="text-xs text-gray-500">
                          Total: {Array.isArray(window.__filteredPolicyList) ? window.__filteredPolicyList.length : 0}
                          {' '}• Selected: {groupFormData.selectedPolicyIds.length}
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className="px-2 py-1 text-xs bg-[#2D869C] text-white rounded hover:bg-[#247180]"
                            onClick={() => {
                              const filtered = Array.isArray(window.__filteredPolicyList) ? window.__filteredPolicyList : [];
                              const filteredIds = filtered.map(p => p._id);
                              setGroupFormData(prev => ({
                                ...prev,
                                selectedPolicyIds: Array.from(new Set([...(prev.selectedPolicyIds || []), ...filteredIds]))
                              }));
                            }}
                          >
                            Select All (filtered)
                          </button>
                          <button
                            type="button"
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200"
                            onClick={() => setGroupFormData(prev => ({ ...prev, selectedPolicyIds: [] }))}
                          >
                            Clear
                          </button>
                        </div>
                      </div>

                      {/* Grouped list */}
                      <div className="max-h-56 overflow-y-auto border border-gray-200 rounded-lg">
                        {(() => {
                          const grouped = window.__policyGroups || {};
                          const groupKeys = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
                          if (groupKeys.length === 0) {
                            return (<div className="p-2 text-xs text-gray-500">No policies available</div>);
                          }
                          return (
                            <div className="divide-y divide-gray-100">
                              {groupKeys.map((key) => (
                                <div key={key} className="p-2">
                                  <div className="flex items-center justify-between mb-1">
                                    <div className="text-xs font-semibold text-gray-700">{key}</div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        type="button"
                                        className="px-2 py-1 text-xs bg-purple-600 text-white rounded hover:bg-purple-700"
                                        onClick={() => {
                                          const ids = (window.__policyGroups?.[key] || []).map(p => p._id);
                                          setGroupFormData(prev => ({
                                            ...prev,
                                            selectedPolicyIds: Array.from(new Set([...(prev.selectedPolicyIds || []), ...ids]))
                                          }));
                                        }}
                                      >
                                        Select Group
                                      </button>
                                      <button
                                        type="button"
                                        className="px-2 py-1 text-xs bg-gray-100 text-gray-700 border border-gray-300 rounded hover:bg-gray-200"
                                        onClick={() => {
                                          const ids = new Set((window.__policyGroups?.[key] || []).map(p => p._id));
                                          setGroupFormData(prev => ({
                                            ...prev,
                                            selectedPolicyIds: (prev.selectedPolicyIds || []).filter(id => !ids.has(id))
                                          }));
                                        }}
                                      >
                                        Clear Group
                                      </button>
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-1 gap-1">
                                    {(window.__policyGroups?.[key] || []).sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(policy => (
                                      <label key={policy._id} className="flex items-center text-xs py-1">
                                        <input
                                          type="checkbox"
                                          className="mr-2 rounded border-gray-300 text-[#2D869C]"
                                          checked={groupFormData.selectedPolicyIds.includes(policy._id)}
                                          onChange={(e) => {
                                            const checked = e.target.checked;
                                            setGroupFormData(prev => ({
                                              ...prev,
                                              selectedPolicyIds: checked
                                                ? [...prev.selectedPolicyIds, policy._id]
                                                : prev.selectedPolicyIds.filter(id => id !== policy._id)
                                            }));
                                          }}
                                        />
                                        <span className="text-gray-700">
                                          {policy.name}
                                          {policy.description ? (
                                            <span className="text-[10px] text-gray-500"> — {policy.description}</span>
                                          ) : null}
                                        </span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
              </div>

              <div className="flex justify-end gap-3 p-6 border-t-2 border-gray-200 flex-shrink-0">
                  <button
                    onClick={closeGroupModals}
                    className="flex items-center gap-2 px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 font-bold text-md transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleEditGroup}
                    disabled={groupActionLoading || !groupFormData.name.trim()}
                    className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] disabled:from-gray-400 disabled:to-gray-400 text-white rounded-xl font-bold text-md shadow-lg hover:shadow-xl disabled:cursor-not-allowed transition-all"
                  >
                    {groupActionLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin" />Updating...</>
                    ) : (
                      'Update Group'
                    )}
                  </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Create User Wizard */}
      <CreateUserWizard
        isOpen={isCreateUserModalOpen}
        onClose={() => setIsCreateUserModalOpen(false)}
        onSuccess={handleCreateUserSuccess}
      />

      {/* Edit User Modal */}
      {
        isEditUserModalOpen && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ fontFamily: 'Poppins' }}>
            <div className="relative bg-white w-11/12 max-w-4xl shadow-2xl rounded-2xl overflow-hidden max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <Edit className="w-5 h-5 text-white" />
                  <h3 className="text-xl font-bold text-white">Edit User: {selectedUser.name || selectedUser.email}</h3>
                </div>
                <button onClick={closeEditUserModal} className="text-white/80 hover:text-white p-1 hover:bg-white/20 rounded-lg transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Error Display */}
              {editUserError && (
                <div className="mx-6 mt-4 border-2 border-red-200 bg-red-50 text-red-700 px-4 py-3 rounded-xl flex items-center gap-3 font-semibold text-md">
                  <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                  <div>{editUserError}</div>
                </div>
              )}

              {/* Form Content */}
              <div className="p-6 overflow-y-auto flex-1">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Basic Information */}
                  <div className="space-y-5">
                    <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
                        <h4 className="text-xl font-bold text-white">Basic Information</h4>
                      </div>
                      <div className="p-5 space-y-4">
                        <div>
                          <label className={LBL}>Email Address <span className="text-red-500 normal-case">*</span></label>
                          <input
                            type="email"
                            value={editUserFormData.email}
                            onChange={(e) => setEditUserFormData({ ...editUserFormData, email: e.target.value })}
                            className={INP}
                          />
                        </div>

                        <div>
                          <label className={LBL}>Full Name <span className="text-red-500 normal-case">*</span></label>
                          <input
                            type="text"
                            value={editUserFormData.name}
                            onChange={(e) => setEditUserFormData({ ...editUserFormData, name: e.target.value })}
                            className={INP}
                          />
                        </div>

                        <div>
                          <Select
                            label="Role *"
                            value={editUserFormData.role}
                            onChange={(value) => setEditUserFormData({ ...editUserFormData, role: value })}
                            options={[
                              { value: "Admin", label: "Admin" },
                              { value: "Doctor", label: "Doctor" },
                              { value: "Receptionist", label: "Receptionist" },
                              { value: "Nurse", label: "Nurse" }
                            ]}
                            placeholder="Select a role"
                            required
                            className="[&>div>button]:border-gray-300 [&>div>button]:focus:ring-[#2D869C] [&>div>button]:focus:border-[#2D869C] [&>div>button]:focus:outline-none [&>div>button]:focus:ring-2"
                          />
                        </div>

                        <div>
                          <Select
                            label="Status *"
                            value={editUserFormData.status}
                            onChange={(value) => setEditUserFormData({ ...editUserFormData, status: value })}
                            options={[
                              { value: "active", label: "Active" },
                              { value: "inactive", label: "Inactive" }
                            ]}
                            placeholder="Select status"
                            required
                            className="[&>div>button]:border-gray-300 [&>div>button]:focus:ring-[#2D869C] [&>div>button]:focus:border-[#2D869C] [&>div>button]:focus:outline-none [&>div>button]:focus:ring-2"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Groups and Permissions */}
                  <div className="space-y-5">
                    {/* Groups Selection */}
                    <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
                        <h4 className="text-xl font-bold text-white">Groups</h4>
                        <p className="text-sm text-white/80">Select groups this user should belong to.</p>
                      </div>
                      <div className="divide-y divide-gray-100 max-h-48 overflow-y-auto">
                        {groups.length > 0 ? (
                          groups.map((group) => (
                            <div key={group._id} className="p-3 hover:bg-gray-50">
                              <label className="flex items-center gap-3 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={editUserFormData.selectedGroups.includes(group._id)}
                                  onChange={() => handleEditUserGroupToggle(group._id)}
                                  className="h-5 w-5 text-[#2D869C] border-2 border-[#2D869C] rounded focus:ring-[#9DC9D4]"
                                />
                                <div>
                                  <p className="text-md font-semibold text-gray-700">{group.name}</p>
                                  <p className="text-sm text-gray-400">{group.description || 'No description'}</p>
                                </div>
                              </label>
                            </div>
                          ))
                        ) : (
                          <div className="p-4 text-center text-gray-500 text-md font-semibold">No groups available</div>
                        )}
                      </div>
                      <div className="px-4 py-2 border-t-2 border-gray-200 bg-gray-50">
                        <span className="text-md font-semibold text-[#2D869C]">Selected: {editUserFormData.selectedGroups.length} group(s)</span>
                      </div>
                    </div>

                    {/* Permissions Selection */}
                    <div className="bg-white rounded-xl border-2 border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-5 py-3">
                        <h4 className="text-xl font-bold text-white">Individual Permissions</h4>
                        <p className="text-sm text-white/80">Select individual permissions for this user.</p>
                      </div>
                      {/* Search */}
                      <div className="p-3 border-b-2 border-gray-200">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Search className="h-4 w-4 text-gray-400" />
                          </div>
                          <input
                            type="text"
                            value={popupSearch}
                            onChange={(e) => setPopupSearch(e.target.value)}
                            placeholder="Search permissions..."
                            className={`${INP} pl-10`}
                          />
                        </div>
                      </div>
                      <div className="divide-y divide-gray-100 max-h-[60vh] overflow-y-auto">
                        {allPermissions.length > 0 ? (
                          allPermissions
                            .filter(p => {
                              if (!popupSearch) return true;
                              const hay = `${p.name || ''} ${p.description || ''} ${p.category || ''}`.toLowerCase();
                              return hay.includes(popupSearch.toLowerCase());
                            })
                            .map((permission) => (
                              <div key={permission._id || permission.id} className="p-3 hover:bg-gray-50">
                                <label className="flex items-center gap-3 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={editUserFormData.selectedPermissions.includes(permission._id || permission.id)}
                                    onChange={() => handleEditUserPermissionToggle(permission._id || permission.id)}
                                    className="h-5 w-5 text-[#2D869C] border-2 border-[#2D869C] rounded focus:ring-[#9DC9D4]"
                                  />
                                  <div>
                                    <p className="text-md font-semibold text-gray-700">{permission.name}</p>
                                    <p className="text-sm text-gray-400">{permission.description || 'No description'}</p>
                                  </div>
                                </label>
                              </div>
                            ))
                        ) : (
                          <div className="p-4 text-center text-gray-500 text-md font-semibold">No permissions available</div>
                        )}
                      </div>
                      <div className="px-4 py-2 border-t-2 border-gray-200 bg-gray-50">
                        <span className="text-md font-semibold text-[#2D869C]">Selected: {editUserFormData.selectedPermissions.length} permission(s)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end px-6 py-4 border-t-2 border-gray-200 gap-3 flex-shrink-0">
                <button
                  onClick={closeEditUserModal}
                  className="flex items-center gap-2 px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 font-bold text-md transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateUser}
                  disabled={userActionLoading}
                  className="flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] hover:from-[#236b7a] hover:to-[#2D869C] disabled:from-gray-400 disabled:to-gray-400 text-white rounded-xl font-bold text-md shadow-lg hover:shadow-xl disabled:cursor-not-allowed transition-all"
                >
                  {userActionLoading ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />Updating...</>
                  ) : (
                    <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>Update User</>
                  )}
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* Permissions Popup Modal */}
      {
        isPermissionsPopupOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ fontFamily: 'Poppins' }}>
            <div className="relative bg-white w-11/12 max-w-5xl shadow-2xl rounded-2xl overflow-hidden max-h-[90vh] flex flex-col">
              {/* Header */}
              <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-3">
                  <Key className="w-5 h-5 text-white" />
                  <h3 className="text-xl font-bold text-white">{popupTitle}</h3>
                </div>
                <button
                  onClick={closePermissionsPopup}
                  className="text-white/80 hover:text-white p-1 hover:bg-white/20 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Permissions List */}
              <div className="p-6 space-y-4 overflow-y-auto flex-1">
                  {/* Search bar */}
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      value={popupSearch}
                      onChange={(e) => setPopupSearch(e.target.value)}
                      placeholder="Search permissions by name, description or category..."
                      className={`${INP} pl-10`}
                    />
                  </div>

                  {popupPermissions.length > 0 ? (
                    <>
                      <div className="mb-2 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <Key className="w-5 h-5 text-orange-600 mr-2" />
                            <div className="text-sm text-orange-800">
                              <strong>Total:</strong> {popupPermissions.length}
                            </div>
                          </div>
                          <div className="text-xs text-gray-600">
                            Showing: {
                              popupSearch
                                ? popupPermissions.filter(p => {
                                  const hay = `${p.name || ''} ${p.description || ''} ${p.category || ''}`.toLowerCase();
                                  return hay.includes(popupSearch.toLowerCase());
                                }).length
                                : popupPermissions.length
                            }
                          </div>
                        </div>
                      </div>

                      {/* Grouped and scrollable list */}
                      <div className="max-h-[70vh] overflow-y-auto border border-gray-200 rounded-lg">
                        {(() => {
                          const list = popupPermissions.filter(p => {
                            if (!popupSearch) return true;
                            const hay = `${p.name || ''} ${p.description || ''} ${p.category || ''}`.toLowerCase();
                            return hay.includes(popupSearch.toLowerCase());
                          });

                          const getGroupKey = (p) => {
                            if (p.category) return p.category;
                            const n = p.name || '';
                            const d = [':', '.', '-'];
                            for (const sep of d) {
                              if (n.includes(sep)) return n.split(sep)[0];
                            }
                            return (n[0] || 'Other').toUpperCase();
                          };

                          const grouped = list.reduce((acc, p) => {
                            const key = getGroupKey(p);
                            if (!acc[key]) acc[key] = [];
                            acc[key].push(p);
                            return acc;
                          }, {});

                          const keys = Object.keys(grouped).sort((a, b) => a.localeCompare(b));
                          if (keys.length === 0) {
                            return <div className="p-4 text-sm text-gray-500">No permissions match your search</div>;
                          }

                          return (
                            <div className="divide-y divide-gray-100">
                              {keys.map((key) => (
                                <div key={key} className="p-3">
                                  <div className="flex items-center justify-between mb-2">
                                    <div className="text-sm font-semibold text-gray-800">{key}</div>
                                    <span className="text-xs text-gray-500">{grouped[key].length}</span>
                                  </div>
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {grouped[key]
                                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                                      .map((permission, index) => (
                                        <div key={permission._id || permission.id} className="p-2 hover:bg-gray-50 rounded">
                                          <div className="flex items-start">
                                            <div className="flex-shrink-0 w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                                              <Key className="w-4 h-4 text-orange-600" />
                                            </div>
                                            <div className="flex-1">
                                              <div className="flex items-center justify-between">
                                                <h4 className="text-sm font-semibold text-gray-900">{permission.name}</h4>
                                                <span className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-orange-100 text-orange-800">#{index + 1}</span>
                                              </div>
                                              {permission.description && (
                                                <p className="text-xs text-gray-600 mt-1">{permission.description}</p>
                                              )}
                                            </div>
                                          </div>
                                        </div>
                                      ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-12">
                      <Key className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 text-lg">No permissions found</p>
                      <p className="text-gray-400 text-sm">Permissions catalog is empty</p>
                    </div>
                  )}
                </div>

              {/* Footer */}
              <div className="flex justify-end gap-3 p-6 border-t-2 border-gray-200 flex-shrink-0">
                <button
                  onClick={closePermissionsPopup}
                  className="flex items-center gap-2 px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 font-bold text-md transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )
      }

      {/* View User Profile Modal */}
      {isViewUserProfileOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ fontFamily: 'Poppins' }}>
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-purple-800 px-6 py-4 flex items-center justify-between flex-shrink-0">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Eye className="w-5 h-5" />
                User Profile
              </h3>
              <button onClick={closeViewUserProfile} className="text-white/80 hover:text-white p-1 hover:bg-white/20 rounded-lg transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto p-6 space-y-6">
              {viewUserProfileLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
                </div>
              ) : viewUserProfileData ? (
                <>
                  {/* Avatar + Basic Info */}
                  <div className="flex items-center gap-5">
                    <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 overflow-hidden border-4 border-purple-200">
                      {viewUserProfileData.profilePicture?.url ? (
                        <img src={viewUserProfileData.profilePicture.url} alt="Profile" className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-2xl font-bold text-purple-600">
                          {(viewUserProfileData.name || viewUserProfileData.email || '?')[0].toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-800">{viewUserProfileData.name || '—'}</h4>
                      <p className="text-sm text-gray-500">{viewUserProfileData.email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold">{viewUserProfileData.role}</span>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                          viewUserProfileData.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>{viewUserProfileData.status?.toUpperCase()}</span>
                      </div>
                    </div>
                  </div>

                  {/* Account Details */}
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    <h5 className="text-sm font-bold text-gray-600 uppercase tracking-wide">Account Details</h5>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase">ID</p>
                        <p className="text-sm font-semibold text-gray-700 break-all">{viewUserProfileData.id}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase">Member Since</p>
                        <p className="text-sm font-semibold text-gray-700">{viewUserProfileData.createdAt ? new Date(viewUserProfileData.createdAt).toLocaleDateString() : '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase">Last Updated</p>
                        <p className="text-sm font-semibold text-gray-700">{viewUserProfileData.updatedAt ? new Date(viewUserProfileData.updatedAt).toLocaleDateString() : '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 font-medium uppercase">Mobile</p>
                        <p className="text-sm font-semibold text-gray-700">{viewUserProfileData.mobile || '—'}</p>
                      </div>
                    </div>
                  </div>

                  {/* Personal Details */}
                  {viewUserProfileData.personalDetails && (
                    <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                      <h5 className="text-sm font-bold text-gray-600 uppercase tracking-wide">Personal Details</h5>
                      <div className="grid grid-cols-2 gap-4">
                        {viewUserProfileData.personalDetails.gender && (
                          <div>
                            <p className="text-xs text-gray-400 font-medium uppercase">Gender</p>
                            <p className="text-sm font-semibold text-gray-700 capitalize">{viewUserProfileData.personalDetails.gender}</p>
                          </div>
                        )}
                        {viewUserProfileData.personalDetails.dateOfBirth && (
                          <div>
                            <p className="text-xs text-gray-400 font-medium uppercase">Date of Birth</p>
                            <p className="text-sm font-semibold text-gray-700">{new Date(viewUserProfileData.personalDetails.dateOfBirth).toLocaleDateString()}</p>
                          </div>
                        )}
                        {(viewUserProfileData.personalDetails.city || viewUserProfileData.personalDetails.country) && (
                          <div>
                            <p className="text-xs text-gray-400 font-medium uppercase">Location</p>
                            <p className="text-sm font-semibold text-gray-700">
                              {[viewUserProfileData.personalDetails.city, viewUserProfileData.personalDetails.state, viewUserProfileData.personalDetails.country].filter(Boolean).join(', ')}
                            </p>
                          </div>
                        )}
                        {viewUserProfileData.personalDetails.address && (
                          <div className="col-span-2">
                            <p className="text-xs text-gray-400 font-medium uppercase">Address</p>
                            <p className="text-sm font-semibold text-gray-700">{viewUserProfileData.personalDetails.address}</p>
                          </div>
                        )}
                      </div>
                      {/* Emergency Contact */}
                      {viewUserProfileData.personalDetails.emergencyContact?.name && (
                        <div className="border-t border-gray-200 pt-3">
                          <p className="text-xs text-gray-400 font-medium uppercase mb-2">Emergency Contact</p>
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <p className="text-xs text-gray-400">Name</p>
                              <p className="text-sm font-semibold text-gray-700">{viewUserProfileData.personalDetails.emergencyContact.name}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400">Phone</p>
                              <p className="text-sm font-semibold text-gray-700">{viewUserProfileData.personalDetails.emergencyContact.phone || '—'}</p>
                            </div>
                            <div>
                              <p className="text-xs text-gray-400">Relationship</p>
                              <p className="text-sm font-semibold text-gray-700">{viewUserProfileData.personalDetails.emergencyContact.relationship || '—'}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Professional Details */}
                  {viewUserProfileData.professionalDetails && (
                    (() => {
                      const pd = viewUserProfileData.professionalDetails;
                      const hasAny = pd.designation || pd.department || pd.specialization || pd.qualification || pd.joiningDate;
                      return hasAny ? (
                        <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                          <h5 className="text-sm font-bold text-gray-600 uppercase tracking-wide">Professional Details</h5>
                          <div className="grid grid-cols-2 gap-4">
                            {pd.designation && <div><p className="text-xs text-gray-400 uppercase">Designation</p><p className="text-sm font-semibold text-gray-700">{pd.designation}</p></div>}
                            {pd.department && <div><p className="text-xs text-gray-400 uppercase">Department</p><p className="text-sm font-semibold text-gray-700">{pd.department}</p></div>}
                            {pd.specialization && <div><p className="text-xs text-gray-400 uppercase">Specialization</p><p className="text-sm font-semibold text-gray-700">{pd.specialization}</p></div>}
                            {pd.qualification && <div><p className="text-xs text-gray-400 uppercase">Qualification</p><p className="text-sm font-semibold text-gray-700">{pd.qualification}</p></div>}
                            {pd.experience > 0 && <div><p className="text-xs text-gray-400 uppercase">Experience</p><p className="text-sm font-semibold text-gray-700">{pd.experience} years</p></div>}
                            {pd.joiningDate && <div><p className="text-xs text-gray-400 uppercase">Joined</p><p className="text-sm font-semibold text-gray-700">{new Date(pd.joiningDate).toLocaleDateString()}</p></div>}
                          </div>
                        </div>
                      ) : null;
                    })()
                  )}

                  {/* Groups */}
                  {viewUserProfileData.groups && viewUserProfileData.groups.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      <h5 className="text-sm font-bold text-gray-600 uppercase tracking-wide">Groups</h5>
                      <div className="flex flex-wrap gap-2">
                        {viewUserProfileData.groups.map(g => (
                          <span key={g._id} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">{g.name}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Permissions */}
                  {viewUserProfileData.permissions && viewUserProfileData.permissions.length > 0 && (
                    <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                      <h5 className="text-sm font-bold text-gray-600 uppercase tracking-wide">Permissions ({viewUserProfileData.permissions.length})</h5>
                      <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                        {viewUserProfileData.permissions.map(p => (
                          <span key={p._id} className="px-2 py-1 bg-green-100 text-green-700 rounded-lg text-xs font-semibold" title={p.description}>{p.name}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16 text-gray-500">Failed to load profile.</div>
              )}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end flex-shrink-0">
              <button
                onClick={closeViewUserProfile}
                className="px-6 py-2.5 text-gray-700 bg-white border-2 border-gray-200 rounded-xl hover:border-gray-400 hover:bg-gray-50 font-bold text-md transition-all"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Activity Detail Modal ─────────────────────────────────────────── */}
      {activityDetailLog && (() => {
        const log = activityDetailLog;
        const d = log.newData || {};
        const inr = (n) => (n !== undefined && n !== null) ? `₹${Number(n).toLocaleString('en-IN')}` : null;

        const Section = ({ title, children }) => (
          <div className="mb-5">
            <h4 className="text-sm font-bold text-[#2D869C] uppercase tracking-wider mb-2 pb-1 border-b border-gray-100">{title}</h4>
            {children}
          </div>
        );
        const Row = ({ label, value }) => value !== undefined && value !== null && value !== '' ? (
          <div className="flex justify-between items-start py-1.5 border-b border-gray-50">
            <span className="text-sm font-semibold text-gray-500 w-2/5 shrink-0">{label}</span>
            <span className="text-sm font-semibold text-gray-900 text-right w-3/5 break-words">{value}</span>
          </div>
        ) : null;

        return (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setActivityDetailLog(null)}>
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              {/* Header */}
              <div className="bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] px-6 py-4 flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-white">Activity Details</h3>
                  <p className="text-sm text-white/80 mt-0.5">{getActivitySummary(log)}</p>
                </div>
                <button onClick={() => setActivityDetailLog(null)} className="text-white/80 hover:text-white p-1 rounded-lg hover:bg-white/10 transition-all">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Body */}
              <div className="overflow-y-auto p-6 flex-1">
                {/* Who & When */}
                <Section title="Who & When">
                  <Row label="Performed By" value={log.userName || log.userEmail} />
                  <Row label="Email" value={log.userEmail} />
                  <Row label="Role" value={log.userRole} />
                  <Row label="Date & Time" value={log.timestamp ? new Date(log.timestamp).toLocaleString('en-IN', { dateStyle: 'long', timeStyle: 'medium' }) : '—'} />
                  <Row label="Status" value={log.success ? '✅ Successful' : '❌ Failed'} />
                </Section>

                {/* What happened */}
                <Section title="What Happened">
                  <Row label="Activity" value={humanizeActivityType(log.activityType)} />
                  <Row label="Summary" value={getActivitySummary(log)} />
                  <Row label="Section" value={humanizeResource(log.resource)} />
                </Section>

                {/* Payment details */}
                {(log.activityType === 'payment_create' || (log.action || '').includes('/payments')) && (
                  <Section title="Payment Details">
                    <Row label="Payment Method" value={d.paymentMethod} />
                    <Row label="Amount Paid" value={inr(d.amount)} />
                  </Section>
                )}

                {/* Invoice / Billing details */}
                {(log.activityType === 'invoice_create' || (d.billingType !== undefined || (d.medicines && !d.items))) && (
                  <Section title="Invoice Details">
                    <Row label="Billing Type" value={d.billingType} />
                    {d.doctorCharges && (
                      <>
                        <Row label="Consultation Type" value={d.doctorCharges.consultationType} />
                        <Row label="Doctor Charges" value={inr(d.doctorCharges.amount)} />
                      </>
                    )}
                    {d.medicines?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-bold text-gray-600 mb-1">Medicines ({d.medicines.length})</p>
                        {d.medicines.map((m, i) => (
                          <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-50">
                            <span className="text-gray-700">{m.medicineName}</span>
                            <span className="text-gray-500">Qty {m.quantity} × {inr(m.unitPrice)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {d.tests?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-bold text-gray-600 mb-1">Lab Tests ({d.tests.length})</p>
                        {d.tests.map((t, i) => (
                          <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-50">
                            <span className="text-gray-700">{t.testName}</span>
                            <span className="text-gray-500">{inr(t.unitPrice)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {d.imagingTests?.length > 0 && (
                      <div className="mt-2">
                        <p className="text-sm font-bold text-gray-600 mb-1">Imaging Tests ({d.imagingTests.length})</p>
                        {d.imagingTests.map((t, i) => (
                          <div key={i} className="flex justify-between text-sm py-1 border-b border-gray-50">
                            <span className="text-gray-700">{t.testName} ({t.modality})</span>
                            <span className="text-gray-500">{inr(t.unitPrice)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </Section>
                )}

                {/* Prescription details */}
                {(log.activityType === 'prescription_create' || (d.items !== undefined && d.patient)) && (
                  <Section title="Prescription Details">
                    {d.items?.length > 0 && (
                      <div className="mb-2">
                        <p className="text-sm font-bold text-gray-600 mb-1">Medicines ({d.items.length})</p>
                        {d.items.map((m, i) => (
                          <div key={i} className="text-sm py-1 border-b border-gray-50">
                            <span className="font-semibold text-gray-800">{m.name}</span>
                            <span className="text-gray-500 ml-2">{m.dose} · {m.frequency} · {m.days} days</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {Array.isArray(d.tests) && d.tests.length > 0 && typeof d.tests[0] === 'string' && (
                      <Row label="Lab Tests Ordered" value={`${d.tests.length} test(s)`} />
                    )}
                    {d.imagingTests?.length > 0 && (
                      <div className="mb-2">
                        <p className="text-sm font-bold text-gray-600 mb-1">Imaging Tests ({d.imagingTests.length})</p>
                        {d.imagingTests.map((t, i) => (
                          <div key={i} className="text-sm py-1 border-b border-gray-50">
                            <span className="font-semibold text-gray-800">{t.modality}</span>
                            {t.selectedViews?.length > 0 && <span className="text-gray-500 ml-2">{t.selectedViews.join(', ')}</span>}
                          </div>
                        ))}
                      </div>
                    )}
                    {d.foodAdvice && <Row label="Food Advice" value={d.foodAdvice} />}
                  </Section>
                )}

                {/* Appointment booking */}
                {((log.action || '').includes('book-appointment') || log.resource === 'book_appointment') && (
                  <Section title="Appointment Details">
                    <Row label="Complaint" value={d.complaint} />
                  </Section>
                )}

                {/* Patient update */}
                {(log.activityType === 'patient_update' || (log.activityType === 'update' && (d.firstName || d.lastName))) && (
                  <Section title="Patient Details Updated">
                    <Row label="Patient Name" value={[d.firstName, d.lastName].filter(Boolean).join(' ')} />
                    <Row label="Date of Birth" value={d.dob ? new Date(d.dob).toLocaleDateString('en-IN') : null} />
                    <Row label="Blood Group" value={d.bloodGroup} />
                    <Row label="Mobile" value={d.mobile} />
                    <Row label="Email" value={d.email} />
                    <Row label="Chief Complaint" value={d.chiefComplaint} />
                    {d.personalAddress?.city && <Row label="City" value={`${d.personalAddress.city}, ${d.personalAddress.state || ''}`} />}
                  </Section>
                )}

                {/* Slogan */}
                {(log.resource === 'slogans' || (log.action || '').includes('/slogans')) && d.slogan && (
                  <Section title="Slogan Details">
                    <Row label="Slogan Text" value={d.slogan} />
                    <Row label="Active" value={d.isActive ? 'Yes' : 'No'} />
                  </Section>
                )}

                {/* Device Info */}
                <Section title="Technical Info">
                  <Row label="IP Address" value={log.ipAddress !== 'unknown' ? log.ipAddress : '—'} />
                  <Row label="Error Message" value={log.errorMessage} />
                </Section>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end">
                <button
                  onClick={() => setActivityDetailLog(null)}
                  className="px-6 py-2.5 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] text-white rounded-xl font-bold text-sm hover:from-[#236b7a] hover:to-[#2D869C] transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      {/* ────────────────────────────────────────────────────────────────────── */}

      {/* ─── Clear Logs Modal ──────────────────────────────────────────────── */}
      {showClearLogsModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) { setShowClearLogsModal(false); } }}
        >
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
            {/* Header */}
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-red-50 to-orange-50">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-xl">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Poppins, sans-serif' }}>Clear Activity Logs</h2>
                  <p className="text-xs text-gray-500 mt-0.5" style={{ fontFamily: 'Poppins, sans-serif' }}>Permanently delete old log entries</p>
                </div>
              </div>
              <button onClick={() => setShowClearLogsModal(false)} className="p-1.5 hover:bg-red-100 rounded-lg transition-colors">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {clearLogsResult ? (
              /* ── Success State ── */
              <div className="px-6 py-10 flex flex-col items-center gap-4 text-center">
                <div className="p-4 bg-green-100 rounded-full">
                  <CheckCircle className="w-10 h-10 text-green-600" />
                </div>
                <div>
                  <p className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Poppins, sans-serif' }}>Logs Cleared Successfully</p>
                  <p className="text-sm text-gray-500 mt-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    <span className="font-bold text-red-600">{clearLogsResult.deletedCount}</span> log {clearLogsResult.deletedCount === 1 ? 'entry' : 'entries'} deleted
                  </p>
                </div>
                <button
                  onClick={() => setShowClearLogsModal(false)}
                  className="mt-2 px-6 py-2.5 bg-gradient-to-r from-[#2D869C] to-[#3a9fb5] text-white rounded-xl font-bold text-sm hover:from-[#236b7a] hover:to-[#2D869C] transition-all"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  Done
                </button>
              </div>
            ) : (
              /* ── Configuration State ── */
              <div className="px-6 py-5 space-y-5">
                {/* Mode Toggle */}
                <div className="flex gap-2 p-1 bg-gray-100 rounded-xl">
                  <button
                    onClick={() => setClearLogsMode('preset')}
                    className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${clearLogsMode === 'preset' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    Quick Presets
                  </button>
                  <button
                    onClick={() => setClearLogsMode('custom')}
                    className={`flex-1 py-2 rounded-lg text-sm font-bold transition-all ${clearLogsMode === 'custom' ? 'bg-white shadow text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}
                    style={{ fontFamily: 'Poppins, sans-serif' }}
                  >
                    Custom Date Range
                  </button>
                </div>

                {clearLogsMode === 'preset' ? (
                  /* ── Preset Buttons ── */
                  <div>
                    <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wide" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Delete logs older than…
                    </p>
                    <div className="grid grid-cols-4 gap-2">
                      {[15, 30, 60, 90].map(d => (
                        <button
                          key={d}
                          onClick={() => setClearLogsDays(d)}
                          className={`py-3 rounded-xl text-sm font-bold border-2 transition-all ${clearLogsDays === d ? 'border-red-400 bg-red-50 text-red-700' : 'border-gray-200 bg-white text-gray-600 hover:border-red-300 hover:bg-red-50'}`}
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        >
                          {d} days
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-500 mt-3 bg-amber-50 border border-amber-200 rounded-lg p-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      <span className="font-bold text-amber-700">Preview: </span>
                      All logs from the beginning up to{' '}
                      <span className="font-bold text-gray-700">
                        {(() => { const d = new Date(); d.setDate(d.getDate() - clearLogsDays); return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); })()}
                      </span>{' '}
                      will be permanently deleted.
                    </p>
                  </div>
                ) : (
                  /* ── Custom Date Range ── */
                  <div className="space-y-3">
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide" style={{ fontFamily: 'Poppins, sans-serif' }}>
                      Select date range to delete
                    </p>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5" style={{ fontFamily: 'Poppins, sans-serif' }}>From Date</label>
                        <input
                          type="date"
                          value={clearLogsDateFrom}
                          max={clearLogsDateTo || new Date().toISOString().split('T')[0]}
                          onChange={e => setClearLogsDateFrom(e.target.value)}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#2D869C] transition-colors"
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1.5" style={{ fontFamily: 'Poppins, sans-serif' }}>To Date</label>
                        <input
                          type="date"
                          value={clearLogsDateTo}
                          min={clearLogsDateFrom}
                          max={new Date().toISOString().split('T')[0]}
                          onChange={e => setClearLogsDateTo(e.target.value)}
                          className="w-full px-3 py-2 border-2 border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#2D869C] transition-colors"
                          style={{ fontFamily: 'Poppins, sans-serif' }}
                        />
                      </div>
                    </div>
                    {clearLogsDateFrom && clearLogsDateTo && (
                      <p className="text-xs text-gray-500 bg-amber-50 border border-amber-200 rounded-lg p-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
                        <span className="font-bold text-amber-700">Preview: </span>
                        Logs from{' '}
                        <span className="font-bold text-gray-700">{new Date(clearLogsDateFrom).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        {' '}to{' '}
                        <span className="font-bold text-gray-700">{new Date(clearLogsDateTo).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                        {' '}will be permanently deleted.
                      </p>
                    )}
                  </div>
                )}

                {/* Warning Banner */}
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl p-3">
                  <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                  <p className="text-xs text-red-700 font-medium" style={{ fontFamily: 'Poppins, sans-serif' }}>
                    This action is <span className="font-bold">irreversible</span>. Deleted logs cannot be recovered. Make sure to export a backup PDF before clearing.
                  </p>
                </div>
              </div>
            )}

            {/* Footer */}
            {!clearLogsResult && (
              <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3">
                <button
                  onClick={() => setShowClearLogsModal(false)}
                  className="px-5 py-2.5 text-sm font-bold text-gray-600 bg-gray-100 rounded-xl hover:bg-gray-200 transition-all"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearLogs}
                  disabled={clearLogsLoading || (clearLogsMode === 'custom' && (!clearLogsDateFrom || !clearLogsDateTo))}
                  className="flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {clearLogsLoading ? (
                    <>
                      <RotateCcw className="w-4 h-4 animate-spin" />
                      Clearing…
                    </>
                  ) : (
                    <>
                      <Trash2 className="w-4 h-4" />
                      Confirm Clear
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* ────────────────────────────────────────────────────────────────────── */}

    </div>
  );
};

export default AccessManagement;
